import React, { Suspense } from "react";
import { createBrowserRouter, Navigate, useLocation, useParams, Outlet, type RouteObject } from "react-router-dom";
import { ProtectedRoute } from "./ProtectedRoute";
import { DBAGuard } from "./guards/DBAGuard";
import { AdminGuard } from "./guards/AdminGuard";
import { DevGuard } from "./guards/DevGuard";
import { AppLayout } from "@/shared/ui/layouts/AppLayout";
import { AuthLayout } from "@/shared/ui/layouts/AuthLayout";
import { ErrorPage } from "@/shared/ui/components/shared/ErrorPage";
import { SeedPhraseRecoveryPage } from "@/features/privacy/ui/SeedPhraseRecoveryPage";
import { Spinner as PageSpinner } from "@/shared/ui/components/Spinner";
import { meetingRoutes } from "@/features/meeting";
import { contextExplorerRoutes } from "@/features/context-explorer";
import { authRoutes } from "@/features/auth/routes";
import { EEGate } from "@/features/admin/components/EEGate";

// War Room Layout (full-screen, no sidebar)
const WarRoomLayout = React.lazy(() =>
  import("@/features/war-room/layouts/WarRoomLayout").then((m) => ({
    default: m.WarRoomLayout,
  }))
);

/**
 * Legacy `/app` prefix redirect. Widespread internal links/navigate() calls
 * across the app still target `/app/agents`, `/app/settings`,
 * `/app?agentId=...`, etc. (leftover from the pre-migration Next.js route
 * structure, where the whole app lived under `/app/*`). A blanket
 * `<Navigate to="/" replace />` here would silently drop the path suffix
 * AND query string, so e.g. "Explore Agents" (`/app/agents`) or "Admin
 * Panel" links would always just bounce back to the chat home — looking
 * like the click did nothing. Strip only the `/app` prefix and preserve
 * everything else instead.
 */
function LegacyAppRedirect() {
  const location = useLocation();
  const rest = location.pathname.replace(/^\/app/, "") || "/";
  return <Navigate to={`${rest}${location.search}${location.hash}`} replace />;
}

function PolicyParamRedirect() {
  const { policyId, id } = useParams<{ policyId?: string; id?: string }>();
  const targetId = policyId || id || '';
  return <Navigate to={targetId ? `/admin/vnp-policy/gateway/${targetId}` : '/admin/policy/policies'} replace />;
}

function AgentRegistryRedirect() {
  const { id } = useParams<{ id?: string }>();
  return <Navigate to={id ? `/admin/agents/registry/${id}` : '/admin/agents/registry'} replace />;
}

function SplatRedirect({ targetBase }: { targetBase: string }) {
  const { '*': splat } = useParams<{ '*': string }>();
  const to = splat ? `${targetBase}/${splat}` : targetBase;
  return <Navigate to={to} replace />;
}

/**
 * React Router v6 Data Router.
 * Replaces Next.js App Router file-based routing.
 *
 * All pages are lazy-loaded via dynamic import().
 */
export const routes: RouteObject[] = [
  // ─── Auth routes (public) ────────────────────────────────────────────────
  // Auth routes extracted to features/auth/routes.tsx (must be spread here, or /auth/* is a 404)
  ...authRoutes,

  // TASK-EU003-002 (BUG-EU003-002) — Slack/Discord account-linking
  // confirmation page. Deliberately top-level, NOT nested under the "/"
  // ProtectedRoute below: ProtectedRoute only preserves `location.pathname`
  // (not the query string) in `state.from` when redirecting to
  // /auth/login, which would silently drop `?token=` before the user gets a
  // chance to log in. BotConnectPage does its own auth-check + redirect
  // (via useAuthInit/useAuthStore) that preserves `pathname + search`
  // instead — see that file's top comment for the full contract.
  {
    path: "connect-bot",
    lazy: () =>
      import("@/features/app/pages/BotConnectPage").then((m) => ({ Component: m.BotConnectPage })),
  },
  {
    path: "auth/git/callback",
    lazy: () =>
      import("@/features/admin/pages/git-management/pages/GitOAuthCallbackPage").then((m) => ({ Component: m.GitOAuthCallbackPage })),
  },
  {
    path: "/recover",
    element: <SeedPhraseRecoveryPage />,
  },

  // ─── App routes (authenticated) ──────────────────────────────────────────
  {
    path: "/",
    element: (
      <ProtectedRoute>
        <AppLayout />
      </ProtectedRoute>
    ),
    errorElement: <ErrorPage />,
    children: [
      {
        // TASK-UI-V4-003 (ui/specs/bugs/ui-v4) — reverted the api-ui-v5 swap to
        // `AppPage.tsx` (backend/specs/bugs/api-ui-v5/tasks/TASK-API-UI5-020-1):
        // that change is BLOCKED (vite build fails hard on a missing export,
        // extractChatImageFileId, reachable once AppPage.tsx's message-render
        // tree is live) and conflicts with the ui-v4 decision to extend
        // `ChatPage.tsx` in place instead of resurrecting `AppPage.tsx` — see
        // SOLUTION-UI-V4-002 for the reasoning. `AppPage.tsx` stays unrouted;
        // CR-013/023/026 get migrated into `ChatPage.tsx` piece by piece via
        // TASK-UI-V4-006/007/008.
        index: true,
        lazy: () =>
          import("@/features/app/pages/ChatPage").then((m) => ({ Component: m.ChatPage })),
      },
      {
        // CR-001 — Dashboard Overview. Independent route; `index` above
        // stays ChatPage (UX decision A, SOL-001-dashboard-overview.md §0).
        path: "dashboard",
        lazy: () =>
          import("@/features/app/pages/DashboardPage").then((m) => ({ Component: m.DashboardPage })),
      },
      // ─── Workspace Routes (CR-WS-006 / CR-WS-011 / CR-WS-031) ─────────────
      {
        path: "ws/:workspaceId",
        lazy: () =>
          import("@/shared/ui/layouts/WorkspaceLayout").then((m) => ({ Component: m.WorkspaceLayout })),
        children: [
          {
            index: true,
            lazy: () =>
              import("@/features/workspace/pages/WorkspaceHomePage").then((m) => ({ Component: m.WorkspaceHomePage })),
          },
          {
            path: "canvas",
            lazy: () =>
              import("@/features/workspace/pages/CanvasPage").then((m) => ({ Component: m.CanvasPage })),
          },
          {
            path: "tasks",
            lazy: () =>
              import("@/features/workspace/pages/TasksPage").then((m) => ({ Component: m.TasksPage })),
          },
          {
            path: "files",
            lazy: () =>
              import("@/features/workspace/pages/FilesPage").then((m) => ({ Component: m.FilesPage })),
          },
          {
            path: "docs",
            lazy: () =>
              import("@/features/workspace/pages/DocsPage").then((m) => ({ Component: m.DocsPage })),
          },
          {
            path: "members",
            lazy: () =>
              import("@/features/workspace/pages/MembersPage").then((m) => ({ Component: m.MembersPage })),
          },
          {
            path: "meetings",
            lazy: () =>
              import("@/features/workspace/pages/MeetingsPage").then((m) => ({ Component: m.MeetingsPage })),
          },
          {
            path: "artifacts",
            lazy: () =>
              import("@/features/workspace/pages/ArtifactsPage").then((m) => ({ Component: m.ArtifactsPage })),
          },
          {
            path: "artifacts/:artifactId",
            lazy: () =>
              import("@/features/workspace/pages/ArtifactDetailPage").then((m) => ({ Component: m.ArtifactDetailPage })),
          },
          {
            path: "settings",
            lazy: () =>
              import("@/features/workspace/pages/WorkspaceSettingsPage").then((m) => ({ Component: m.WorkspaceSettingsPage })),
          },
          {
            path: "settings/approvals",
            lazy: () =>
              import("@/features/workspace/pages/PendingApprovalsPage").then((m) => ({ Component: m.PendingApprovalsPage })),
          },
          {
            path: "quota",
            lazy: () =>
              import("@/features/workspace/pages/QuotaSettingsPage").then((m) => ({ Component: m.QuotaSettingsPage })),
          },
          {
            path: "agent",
            lazy: () =>
              import("@/features/workspace/pages/AgentPage").then((m) => ({ Component: m.AgentPage })),
          },
          {
            path: "plans/okr",
            lazy: () =>
              import("@/features/plans/pages/OKRCascadePage").then((m) => ({
                Component: m.OKRCascadePage,
              })),
          },
        ],
      },
      {
        path: "invite/:token",
        lazy: () =>
          import("@/features/auth/pages/InviteAcceptPage").then((m) => ({ Component: m.InviteAcceptPage })),
      },
      {
        path: "ws/new",
        lazy: () =>
          import("@/features/workspace/pages/NewWorkspacePage").then((m) => ({ Component: m.NewWorkspacePage })),
      },
      {
        path: "workspaces/new",
        lazy: () =>
          import("@/features/workspace/pages/NewWorkspacePage").then((m) => ({ Component: m.NewWorkspacePage })),
      },
      {
        path: "admin/compliance",
        lazy: () =>
          import("@/features/admin/pages/AdminCompliancePage").then((m) => ({ Component: m.AdminCompliancePage })),
      },
      // Legacy redirects (SOL-WS-006 §3.4)
      { path: "my-space", element: <Navigate to="/ws/personal" replace /> },
      { path: "workplace", element: <Navigate to="/ws/default" replace /> },
      // ─── Workflows (v9) ──────────────────────────────────────────────────
      {
        path: "workflows",
        lazy: () =>
          import("@/features/workflows/pages/WorkflowListPage").then((m) => ({ Component: m.default })),
      },
      {
        path: "workflows/new",
        lazy: () =>
          import("@/features/workflows/pages/WorkflowBuilderPage").then((m) => ({ Component: m.default })),
      },
      {
        path: "workflows/templates",
        lazy: () =>
          import("@/features/workflows/pages/TemplateLibraryPage").then((m) => ({ Component: m.default })),
      },
      {
        path: "workflows/:id",
        lazy: () =>
          import("@/features/workflows/pages/WorkflowDetailPage").then((m) => ({ Component: m.default })),
      },
      {
        path: "workflows/:id/edit",
        lazy: () =>
          import("@/features/workflows/pages/WorkflowBuilderPage").then((m) => ({ Component: m.default })),
      },
      {
        path: "workflows/:id/executions/:execId",
        lazy: () =>
          import("@/features/workflows/pages/ExecutionDetailPage").then((m) => ({ Component: m.default })),
      },
      {
        path: "workflows/:id/settings",
        lazy: () =>
          import("@/features/workflows/pages/WorkflowSettingsPage").then((m) => ({ Component: m.default })),
      },
      {
        path: "admin/workflows/dlq",
        lazy: () =>
          import("@/features/workflows/pages/DeadLetterQueuePage").then((m) => ({ Component: m.default })),
      },
      {
        // Was pointing at src/pages/app/AgentsPage.tsx — a barebones Phase-1
        // stub (raw <div>s, undefined CSS custom properties so it rendered
        // unstyled, and cards with only an "Edit" link — no way to start a
        // chat or pin the agent). src/refresh-pages/AgentsNavigationPage.tsx
        // is a complete, already-built replacement (search/filter/tabs,
        // AgentCard with Start Chat/Edit/Share/Pin) that was never wired to
        // any route.
        path: "agents",
        lazy: () =>
          import("@/features/agent/pages/AgentsNavigationPage").then((m) => ({
            Component: m.default,
          })),
      },
      {
        path: "agents/create",
        lazy: () =>
          import("@/features/app/pages/AgentEditorPage").then((m) => ({ Component: m.AgentEditorPage })),
      },
      {
        path: "agents/:id/edit",
        lazy: () =>
          import("@/features/app/pages/AgentEditorPage").then((m) => ({ Component: m.AgentEditorPage })),
      },
      {
        // BUG-GOOG-007 FIX: alias /agents/new → AgentEditorPage
        // Test E2E navigates to /agents/new; canonical path is /admin/runtime-agents/new.
        // This alias allows tests to reach AgentEditorPage without changing test selectors.
        // implements: CRS-GOOG-007
        path: "agents/new",
        lazy: () =>
          import("@/features/app/pages/AgentEditorPage").then((m) => ({ Component: m.AgentEditorPage })),
      },
      {
        // TASK-ALM-001: Daemon Agents Management Page
        path: "my-agents/daemons",
        lazy: () =>
          import("@/features/daemon-agents/components/DaemonAgentsPage").then((m) => ({
            Component: m.DaemonAgentsPage,
          })),
      },
      {
        // Fixed 2026-07-26 (CR-032 e2e QA, TASK-CR-032-004): was pointing at
        // the stub `@/features/app/pages/SettingsPage` ("đang được phát triển").
        // The real implementation — General/ChatPreferences(incl. CR-032
        // Memories)/Account/Connectors — lives in `@/features/settings/pages/SettingsPage`
        // but was never wired into any route. See file header comment there.
        path: "settings",
        lazy: () =>
          import("@/features/settings/pages/SettingsPage").then((m) => ({ Component: m.SettingsPage })),
      },
      {
        path: "settings/ai",
        lazy: () =>
          import("@/features/settings/pages/SettingsPage").then((m) => ({ Component: m.SettingsPage })),
      },
      {
        path: "settings/ai-preferences",
        lazy: () =>
          import("@/features/ai-preferences/components/AiPreferencesPage").then((m) => ({ Component: m.AiPreferencesPage })),
      },
      {
        path: "settings/ai-activity",
        lazy: () =>
          import("@/features/ai-preferences/components/AiActivityPage").then((m) => ({ Component: m.AiActivityPage })),
      },
      {
        path: "settings/memory",
        lazy: () =>
          import("@/features/memory-governance/components/MemoryGovernancePage").then((m) => ({ Component: m.MemoryGovernancePage })),
      },
      {
        path: "settings/connectors",
        lazy: () =>
          import("@/features/settings/pages/SettingsPage").then((m) => ({ Component: m.SettingsPage })),
      },
      // BUG-GOOG-003 FIX: OAuth Connections tab trong Settings
      // implements: CRS-GOOG-003
      {
        path: "settings/connections",
        lazy: () =>
          import("@/features/settings/pages/SettingsPage").then((m) => ({ Component: m.SettingsPage })),
      },
      {
        path: "connectors",
        lazy: () =>
          import("@/features/admin/pages/ConnectorsPage").then((m) => ({ Component: m.ConnectorsPage })),
      },
      {
        path: "connectors/hub",
        lazy: () =>
          import("@/features/admin/pages/ConnectorHubPage").then((m) => ({
            Component: m.ConnectorHubPage,
          })),
      },
      {
        path: "connectors/new",
        lazy: () =>
          import("@/features/admin/pages/ConnectorSetupPage").then((m) => ({
            Component: m.ConnectorSetupPage,
          })),
      },
      // BUG-GOOG-001 FIX: Sync Monitor Dashboard route
      // implements: CRS-GOOG-001
      {
        path: "sync",
        lazy: () =>
          import("@/features/connectors/pages/SyncMonitorPage").then((m) => ({
            Component: m.SyncMonitorPage,
          })),
      },
      {
        path: "code-sandbox",
        lazy: () =>
          import("@/features/code/pages/CodeDashboardPage").then((m) => ({ Component: m.CodeDashboardPage })),
      },
      {
        path: "mcp",
        lazy: () =>
          import("@/shared/ui/sections/actions/MCPPageContent").then((m) => ({ Component: m.default })),
      },
      {
        path: "mcp/agents",
        lazy: () =>
          import("@/features/admin/pages/mcp-agents/AgentListPage").then((m) => ({ Component: m.AgentListPage })),
      },
      {
        path: "settings/git-accounts",
        lazy: () =>
          import("@/features/admin/pages/git-management/pages/GitAccountsSettingsPage").then((m) => ({ Component: m.GitAccountsSettingsPage })),
      },
      {
        path: "settings/privacy",
        lazy: () =>
          import("@/features/settings/pages/PrivacySettingsPage").then((m) => ({ Component: m.default })),
      },
      {
        path: "settings/connectors/:serviceId/shares",
        lazy: () =>
          import("@/features/settings/pages/ConnectorSharesPage").then((m) => ({ Component: m.ConnectorSharesPage })),
      },
      {
        // UI-SOL-PE-014 (v6/privacy-enhance ZKPA, Sprint 7) — was a real,
        // closed, self-referencing component trio (AgentDashboard.tsx +
        // AgentPairingModal.tsx + useDesktopAgent.ts + agentChannel.ts) with
        // zero route anywhere in this file. See DesktopAgentPage.tsx's own
        // header comment for backend-readiness caveats.
        path: "settings/desktop-agent",
        lazy: () =>
          import("@/features/settings/pages/DesktopAgentPage").then((m) => ({ Component: m.DesktopAgentPage })),
      },
      {
        // TASK-UI-SKILLS-004 (docs/crs/v9/skills-management) — personal skill
        // management page: create, edit, delete, fork, share personal skills.
        // Canonical URL: /settings/skills. Also accessible at /settings/my-skills
        // for legacy nav links (both serve the same MySkillsPage component).
        path: "settings/skills",
        lazy: () =>
          import("@/features/skills/pages/MySkillsPage").then((m) => ({
            Component: m.MySkillsPage,
          })),
      },
      {
        // /settings/skills/shared-with-me — skills shared with the current user
        // by other workspace members (TASK-UI-SKILLS-005, CRS v9).
        path: "settings/skills/shared-with-me",
        lazy: () =>
          import("@/features/skills/pages/SharedSkillsPage").then((m) => ({
            Component: m.SharedSkillsPage,
          })),
      },
      {
        // TASK-UI-SKILLS-004 (docs/crs/v9/skills-management) — personal skill
        // management page: create, edit, delete, fork, share personal skills.
        path: "settings/my-skills",
        lazy: () =>
          import("@/features/skills/pages/MySkillsPage").then((m) => ({
            Component: m.MySkillsPage,
          })),
      },
      {
        // TASK-PERS-003-F-4: Personal Space Settings
        path: "settings/personal-space/lifecycle",
        lazy: () =>
          import("@/features/personal/pages/LifecycleSettingsPage").then((m) => ({
            Component: m.LifecycleSettingsPage,
          })),
      },
      {
        // SK-07 Memory Bank (TASK-SK-007-004): user knowledge notes settings.
        path: "settings/memory-bank",
        lazy: () =>
          import("@/features/settings/ui/skills/MemoryBankSettings").then((m) => ({
            Component: m.MemoryBankSettings,
          })),
      },
      {
        // SK-06 Skills Catalog (TASK-SK-006-003): browse skills with trigger info.
        path: "settings/skills-catalog",
        lazy: () =>
          import("@/features/settings/ui/skills/SkillsCatalog").then((m) => ({
            Component: m.SkillsCatalog,
          })),
      },
      {
        path: "settings/templates",
        lazy: () =>
          import("@/features/settings/templates/TemplateListPage").then((m) => ({ Component: m.TemplateListPage })),
      },
      {
        path: "settings/templates/new",
        lazy: () =>
          import("@/features/settings/templates/TemplateEditorPage").then((m) => ({ Component: m.TemplateEditorPage })),
      },
      {
        path: "settings/templates/:id/edit",
        lazy: () =>
          import("@/features/settings/templates/TemplateEditorPage").then((m) => ({ Component: m.TemplateEditorPage })),
      },
      // ─── Distributed Search (v9) — TASK-UI-DSEARCH-ROUTER ──────────────────
      {
        path: "search",
        lazy: () =>
          import("@/features/distributed-search/pages/SearchPage").then((m) => ({
            Component: m.SearchPage,
          })),
      },
      {
        path: "search/requests",
        lazy: () =>
          import("@/features/distributed-search/pages/AccessRequestsPage").then((m) => ({
            Component: m.AccessRequestsPage,
          })),
      },
      {
        path: "search/web",
        lazy: () =>
          import("@/features/distributed-search/pages/SearchPage").then((m) => ({
            Component: m.SearchPage,
          })),
      },
      {
        path: "settings/desktop-agents",
        lazy: () =>
          import("@/features/distributed-search/settings/DesktopAgentsPage").then((m) => ({
            Component: m.DesktopAgentsPage,
          })),
      },
      {
        // ── Agent Registry user pages — TASK-UI-AR-016 (v9/agent-registry) ──
        // /settings/agents       → MyAgentsPage (all agents scoped to user)
        // /settings/agents/pair  → DesktopPairingPage (deep-link from desktop app)
        path: "settings/agents",
        lazy: () =>
          import("@/features/agent-registry/pages/MyAgentsPage").then((m) => ({
            Component: m.MyAgentsPage,
          })),
      },
      {
        path: "settings/agents/pair",
        lazy: () =>
          import("@/features/agent-registry/pages/DesktopPairingPage").then((m) => ({
            Component: m.DesktopPairingPage,
          })),
      },
      {
        path: "settings/desktop-agent/consent",
        lazy: () =>
          import("@/features/distributed-search/pages/ConsentSetupPage").then((m) => ({
            Component: m.ConsentSetupPage,
          })),
      },
      {
        path: "projects",
        lazy: () =>
          import("@/features/app/pages/ProjectsPage").then((m) => ({ Component: m.ProjectsPage })),
      },
      {
        path: "help",
        lazy: () =>
          import("@/features/app/pages/HelpFAQPage").then((m) => ({ Component: m.HelpFAQPage })),
      },
      // V19 Context Explorer (SOL-UI-CE-001): tree | chat | detail
      ...contextExplorerRoutes,
      // NEW — CR-002: Drive Folder Tree Explorer
      {
        path: "workspace",
        lazy: () =>
          import("@/features/app/pages/WorkspaceExplorerPage").then((m) => ({
            Component: m.WorkspaceExplorerPage,
          })),
      },
      // TASK-UI-PKB-010: PIKB Products Page
      {
        path: "products",
        lazy: () =>
          import("@/features/products/ui/PIKBProductsPage").then((m) => ({
            Component: m.PIKBProductsPage,
          })),
      },
      {
        path: "my-space",
        lazy: () =>
          import("@/features/my-space/pages/MySpaceDashboard").then((m) => ({ Component: m.MySpaceDashboard })),
      },
      ...meetingRoutes.map(r => {
        // If route path starts with 'my-space/' or 'settings/', keep it absolute relative to '/'
        // by spreading it directly. However, in meetingRoutes some paths are 'meetings', 'meetings/create'.
        // We will prefix those with 'my-space/' to match the legacy path expectations.
        const path = r.path || ''
        if (path.startsWith('settings/') || path.startsWith('my-space/') || path.startsWith('team/')) {
          return r
        }
        return {
          ...r,
          path: `my-space/${path}`
        }
      }),
      {
        // ── Meetings Series Management (Wave 8, CR-MTG-Series) ──
        path: "meetings/series",
        lazy: () =>
          import("@/features/meeting/pages/SeriesManagePage").then((m) => ({ Component: m.SeriesManagePage })),
      },
      {
        path: "shared-with-me",
        lazy: () =>
          import("@/features/shared-with-me/pages/SharedWithMePage").then((m) => ({ Component: m.SharedWithMePage })),
      },
      {
        path: "openwork",
        lazy: () =>
          import("@/features/openwork/pages/OpenWorkPage").then((m) => ({ Component: m.OpenWorkPage })),
      },
      {
        path: "openwork/migrate",
        lazy: () =>
          import("@/features/openwork/pages/MigrateSessionsPage").then((m) => ({ Component: m.MigrateSessionsPage })),
      },
      {
        path: "openwork/session/:sessionId",
        lazy: () =>
          import("@/features/openwork/pages/OpenWorkShell").then((m) => ({ Component: () => {
            // Minimal wrapper — reads sessionId from params
            const { OpenWorkShell } = m
            const { sessionId } = useParams<{ sessionId: string }>()
            return <OpenWorkShell sessionId={sessionId!} />
          }})),
      },
      {
        path: "openwork/:workspaceId",
        lazy: () =>
          import("@/features/openwork/pages/OpenWorkShell").then((m) => ({ Component: () => {
            const { OpenWorkShell } = m
            const { workspaceId } = useParams<{ workspaceId: string }>()
            return <OpenWorkShell sessionId={workspaceId!} />
          }})),
      },
      {
        path: "settings/personal",
        lazy: () =>
          import("@/features/settings/pages/personal/PersonalSettingsLayout").then((m) => ({ Component: m.PersonalSettingsLayout })),
        children: [
          {
            index: true,
            lazy: () => import("@/features/settings/pages/personal/PreferencesPage").then((m) => ({ Component: m.PreferencesPage })),
          },
          {
            path: "privacy",
            lazy: () => import("@/features/settings/pages/personal/PrivacySettingsPage").then((m) => ({ Component: m.PrivacySettingsPage })),
          },
          {
            path: "notifications",
            lazy: () => import("@/features/settings/pages/personal/NotificationsPage").then((m) => ({ Component: m.NotificationsPage })),
          },
          {
            // CR-MCPPERS-001 — per-user personal MCP servers (Enterprise Edition).
            path: "mcp-servers",
            lazy: () => import("@/features/settings/pages/personal/McpServersPage").then((m) => ({ Component: m.McpServersPage })),
          },
          {
            path: "export",
            lazy: () => import("@/features/settings/pages/personal/DataExportPage").then((m) => ({ Component: m.DataExportPage })),
          },
        ]
      },
      {
        path: "settings/archfusion",
        lazy: () =>
          import("@/features/architecture/pages/ArchFusionSettingsPage").then((m) => ({ Component: m.ArchFusionSettingsPage })),
      },
      {
        // CR-003 — Knowledge Catalog (browse/manage discovered data sources).
        path: "catalog",
        lazy: () =>
          import("@/features/app/pages/CatalogPage").then((m) => ({ Component: m.CatalogPage })),
      },
      {
        // SOL-054 (TASK-054-5 §2) — top-level, NOT nested under
        // "/settings/openwork-backup" as SOL-054 §5 originally assumed:
        // the real router has no nested-route-under-settings precedent
        // (SettingsPage is a monolithic component managing its own tabs
        // internally, not a parent route with an <Outlet>). This page does
        // the same — 4 tabs managed internally via Tabs, same as
        // OpenworkBackupPage.tsx's own implementation (TASK-054-2).
        path: "openwork-backup",
        lazy: () =>
          import("@/features/app/pages/OpenworkBackupPage").then((m) => ({
            Component: m.OpenworkBackupPage,
          })),
      },
      {
        path: "graph",
        lazy: () =>
          import("@/features/app/pages/GraphExplorerPage").then((m) => ({ Component: m.GraphExplorerPage })),
      },
      {
        // CR-027 Change 4 — public/org share view. URL thật là
        // "/app/shared/:chatId" (buildShareLink() trong ShareChatSessionModal.tsx)
        // — tới được đây qua LegacyAppRedirect catch-all bên dưới.
        path: "shared/:chatId",
        lazy: () =>
          import("@/features/app/pages/SharedChatPage").then((m) => ({ Component: m.SharedChatPage })),
      },
      // ═══════════════════════════════════════════════════════════════
      // Workplace Context Routes — CR-UI6-03
      // ═══════════════════════════════════════════════════════════════
      {
        path: 'workplace',
        errorElement: <ErrorPage />,
        children: [
          {
            index: true,
            lazy: () =>
              import('@/features/workplace/pages/WorkplaceOverviewPage').then(m => ({
                Component: m.WorkplaceOverviewPage,
              })),
          },
          {
            path: 'chat',
            lazy: () =>
              import('@/features/app/pages/ChatPage').then(m => ({ Component: m.ChatPage })),
          },
          {
            path: 'teams',
            lazy: () =>
              import('@/features/workplace/pages/TeamsPage').then(m => ({ Component: m.TeamsPage })),
          },
          {
            path: 'members',
            lazy: () =>
              import('@/features/workplace/pages/MembersPage').then(m => ({ Component: m.MembersPage })),
          },
          // ═══════════════════════════════════════════════════════════════
          // Project Management (CR-PROJ-002..008) — TDD-UI-09 §1 route list.
          // 2026-09-02: was SharedProjectsPage, an unrelated 2-line
          // placeholder (no phases/risks/deliverables/health concepts) that
          // predates this CR — see backend/specs/crs/v4/project-management/
          // tasks/README.md's dated changelog entry for the rest of the fix
          // (api-gateway route registration to project-service). More
          // specific static route ('projects/portfolio') is listed before
          // the dynamic 'projects/:id' so it isn't swallowed by the :id
          // param match.
          // ═══════════════════════════════════════════════════════════════
          {
            path: 'projects',
            lazy: () =>
              import('@/features/projects/pages/ProjectListPage').then(m => ({ Component: m.ProjectListPage })),
          },
          {
            path: 'projects/portfolio',
            lazy: () =>
              import('@/features/projects/pages/PortfolioDashboardPage').then(m => ({ Component: m.PortfolioDashboardPage })),
          },
          {
            path: 'projects/:id',
            lazy: () =>
              import('@/features/projects/pages/ProjectDetailPage').then(m => ({
                Component: m.ProjectDetailPage,
              })),
          },
          {
            path: 'projects/:id/analytics',
            lazy: () =>
              import('@/features/projects/pages/ProjectAnalyticsPage').then(m => ({
                Component: m.ProjectAnalyticsPage,
              })),
          },
          // ═══════════════════════════════════════════════════════════════
          // Campaign Routes — SOLUTION-005 / BUG-MISSING-V1-005, 006
          // TDD-UI-09 §1, TDD-UI-17 §5
          // ═══════════════════════════════════════════════════════════════
          {
            path: 'campaigns',
            lazy: () =>
              import('@/features/campaigns/pages/CampaignListPage').then(m => ({
                Component: m.CampaignListPage,
              })),
          },
          {
            path: 'assistants',
            lazy: () =>
              import('@/features/assistants/pages/AssistantsListPage').then(m => ({
                Component: m.AssistantsListPage,
              })),
          },
          {
            path: 'code',
            lazy: () => import('@/features/code-sandbox/pages/CodeSandboxPage').then((m) => ({ Component: m.default })),
          },
          {
            // ⚠️ named paths TRƯỚC :id
            path: 'campaigns/personal',
            lazy: () =>
              import('@/features/campaigns/pages/PersonalDashboardPage').then(m => ({
                Component: m.PersonalDashboardPage,
              })),
          },
          {
            path: 'campaigns/enterprise',
            lazy: () =>
              import('@/features/campaigns/pages/EnterpriseDashboardPage').then(m => ({
                Component: m.EnterpriseDashboardPage,
              })),
          },
          {
            // ⚠️ Phải đặt TRƯỚC 'campaigns/:id'
            path: 'campaigns/calendar',
            lazy: () =>
              import('@/features/campaigns/pages/CampaignCalendarPage').then(m => ({
                Component: m.CampaignCalendarPage,
              })),
          },
          {
            // ⚠️ Phải đặt TRƯỚC 'campaigns/:id'
            path: 'campaigns/personal-dashboard',
            lazy: () =>
              import('@/features/campaigns/pages/PersonalDashboardPage').then(m => ({
                Component: m.PersonalDashboardPage,
              })),
          },
          {
            // ⚠️ :id PHẢI sau personal và enterprise
            path: 'campaigns/:id',
            lazy: () =>
              import('@/features/campaigns/pages/CampaignDetailPage').then(m => ({
                Component: m.CampaignDetailPage,
              })),
          },
          {
            path: 'plans',
            lazy: () =>
              import('@/features/plans/pages/PlanListPage').then(m => ({ Component: m.PlanListPage })),
          },
          {
            path: 'plans/enterprise',
            lazy: () =>
              import('@/features/plans/pages/EnterpriseDashboardPage').then(m => ({ Component: m.EnterpriseDashboardPage })),
          },
          {
            path: 'plans/okr',
            lazy: () =>
              import('@/features/plans/pages/OKRCascadePage').then(m => ({ Component: m.OKRCascadePage })),
          },
          // ═══════════════════════════════════════════════════════════════
          // Plans Additional Routes — SOLUTION-003 / BUG-MISSING-V1-003
          // ═══════════════════════════════════════════════════════════════
          {
            // Personal plans dashboard
            path: 'plans/personal',
            lazy: () =>
              import('@/features/plans/pages/PersonalDashboardPage').then(m => ({
                Component: m.PersonalDashboardPage,
              })),
          },
          {
            // Plan detail — ⚠️ PHẢI đặt CUỐI trong nhóm plans/
            // Sau: plans, plans/enterprise, plans/okr, plans/personal
            path: 'plans/:id',
            lazy: () =>
              import('@/features/plans/pages/PlanDetailPage').then(m => ({
                Component: m.PlanDetailPage,
              })),
          },
          {
            path: 'catalog',
            lazy: () =>
              import('@/features/app/pages/CatalogPage').then(m => ({ Component: m.CatalogPage })),
          },
          {
            path: 'settings',
            lazy: () =>
              import('@/features/workplace/pages/WorkplaceSettingsPage').then(m => ({ Component: m.WorkplaceSettingsPage })),
          },
          // ═══════════════════════════════════════════════════════════════
          // Workplace Additional Routes — TDD-UI-17 (departments, orca-tasks, shared)
          // ═══════════════════════════════════════════════════════════════
          {
            path: 'departments',
            lazy: () =>
              import('@/features/workplace/pages/DepartmentsPage').then(m => ({ Component: m.DepartmentsPage })),
          },
          {
            path: 'shared',
            lazy: () =>
              import('@/features/workplace/pages/SharedWithTeamPage').then(m => ({ Component: m.SharedWithTeamPage })),
          },
          {
            path: 'files',
            lazy: () =>
              import('@/features/mcp-files/pages/MCPFilesPage').then(m => ({ Component: m.default })),
          },
          {
            // Orca task detail — navigated from AgentEventsProvider toast
            path: 'orca-tasks/:id',
            lazy: () =>
              import('@/features/workplace/pages/OrcaTaskDetailPage').then(m => ({ Component: m.OrcaTaskDetailPage })),
          },
        ],
      },
      // ═══════════════════════════════════════════════════════════════
      // Mail Client Routes — TDD-UI-38 (v7)
      // ═══════════════════════════════════════════════════════════════
      {
        path: 'mail',
        children: [
          {
            index: true,
            lazy: () =>
              import('@/features/mail/pages/MailInboxPage').then(m => ({
                Component: m.MailInboxPage,
              })),
          },
          {
            path: 'search',
            lazy: () =>
              import('@/features/mail/pages/MailSearchPage').then(m => ({
                Component: m.MailSearchPage,
              })),
          },
          {
            path: 'compose',
            lazy: () =>
              import('@/features/mail/pages/MailComposePage').then(m => ({
                Component: m.MailComposePage,
              })),
          },
          {
            path: 'contacts',
            lazy: () =>
              import('@/features/mail/pages/MailContactsPage').then(m => ({
                Component: m.MailContactsPage,
              })),
          },
          {
            path: 'backup',
            lazy: () =>
              import('@/features/mail/pages/MailBackupPage').then(m => ({
                Component: m.MailBackupPage,
              })),
          },
          {
            path: 'account-settings',
            lazy: () =>
              import('@/features/mail/pages/MailAccountSettingsPage').then(m => ({
                Component: m.MailAccountSettingsPage,
              })),
          },
        ],
      },
      // ═══════════════════════════════════════════════════════════════
      // Code Context Routes — CR-UI6-04
      // ═══════════════════════════════════════════════════════════════
      {
        path: 'code',
        errorElement: <ErrorPage />,
        children: [
          {
            index: true,
            lazy: () =>
              import('@/features/code/pages/CodeDashboardPage').then(m => ({ Component: m.CodeDashboardPage })),
          },
          {
            path: 'chat',
            lazy: () =>
              import('@/features/app/pages/ChatPage').then(m => ({ Component: m.ChatPage })),
          },
          {
            path: 'mcp-agents',
            lazy: () =>
              import('@/features/code/pages/MCPAgentsPage').then(m => ({ Component: m.MCPAgentsPage })),
          },
          {
            // user-facing MCP tool catalog (not admin) — TDD-UI-18
            path: 'mcp-tools',
            lazy: () =>
              import('@/features/code/pages/MCPToolsPage').then(m => ({ Component: m.MCPToolsPage })),
          },
          {
            path: 'workers',
            lazy: () =>
              import('@/features/code/pages/WorkerGroupPage').then(m => ({ Component: m.WorkerGroupPage })),
          },
          {
            path: 'plugins',
            lazy: () =>
              import('@/features/code/pages/PluginRegistryPage').then(m => ({ Component: m.PluginRegistryPage })),
          },
          {
            path: 'catalog',
            lazy: () =>
              import('@/features/code/pages/TechCatalogPage').then(m => ({ Component: m.TechCatalogPage })),
          },
          {
            path: 'diagnostics',
            lazy: () =>
              import('@/features/code/pages/DiagnosticsPage').then(m => ({ Component: m.DiagnosticsPage })),
          },
          {
            path: 'settings',
            lazy: () =>
              import('@/features/code/pages/CodeSettingsPage').then(m => ({ Component: m.CodeSettingsPage })),
          },
          {
            // Code Interpreter — TDD-UI-18 §interpreter
            path: 'interpreter',
            lazy: () =>
              import('@/features/code/pages/CodeInterpreterPage').then(m => ({ Component: m.CodeInterpreterPage })),
          },
          {
            // Coding agents — user-facing, TDD-UI-18 §coding-agents
            path: 'agents',
            lazy: () =>
              import('@/features/code/pages/CodingAgentsPage').then(m => ({ Component: m.CodingAgentsPage })),
          },
          // Shortcuts to existing pages (no new component)
          { path: 'openwork', element: <Navigate to="/openwork" replace /> },
          { path: 'backup', element: <Navigate to="/openwork-backup" replace /> },
        ],
      },
      // ═══════════════════════════════════════════════════════════════
      // Task Management Routes — SOLUTION-001 / BUG-MISSING-V1-001
      // TDD-UI-09 §1: /tasks, /tasks/:id, /tasks/team/:teamId, etc.
      // ═══════════════════════════════════════════════════════════════
      {
        path: 'tasks',
        children: [
          {
            index: true,
            lazy: () =>
              import('@/features/tasks/pages/MyTaskBoardPage').then(m => ({
                Component: m.MyTaskBoardPage,
              })),
          },
          {
            path: 'groups/:groupId',
            lazy: () =>
              import('@/features/tasks/pages/GroupBoardPage').then(m => ({
                Component: m.GroupBoardPage,
              })),
          },
          {
            path: 'team/:teamId',
            lazy: () =>
              import('@/features/tasks/pages/TeamTaskBoardPage').then(m => ({
                Component: m.TeamTaskBoardPage,
              })),
          },
          {
            path: 'department/:deptId',
            lazy: () =>
              import('@/features/tasks/pages/DepartmentDashboardPage').then(m => ({
                Component: m.DepartmentDashboardPage,
              })),
          },
          {
            path: 'business-unit/:buId',
            lazy: () =>
              import('@/features/tasks/pages/BUDashboardPage').then(m => ({
                Component: m.BUDashboardPage,
              })),
          },
          {
            // ⚠️ Phải đặt TRƯỚC ':id' — nếu không "eisenhower" bị match bởi :id
            path: 'eisenhower',
            lazy: () =>
              import('@/features/tasks/pages/MyTaskBoardPage').then(m => ({
                Component: () => {
                  const C = m.MyTaskBoardPage;
                  return <C initialView="eisenhower" />;
                },
              })),
          },
          {
            // ⚠️ :id PHẢI đặt cuối cùng để không conflict với named paths trên
            path: ':id',
            lazy: () =>
              import('@/features/tasks/pages/TaskDetailPage').then(m => ({
                Component: m.TaskDetailPage,
              })),
          },
        ],
      },
      // ═══════════════════════════════════════════════════════════════
      // Work History Routes — SOLUTION-002 / BUG-MISSING-V1-002
      // ═══════════════════════════════════════════════════════════════
      {
        path: 'work-history',
        children: [
          {
            index: true,
            lazy: () =>
              import('@/features/work-history/pages/WorkHistoryPage').then(m => ({
                Component: m.WorkHistoryPage,
              })),
          },
          {
            path: ':id',
            lazy: () =>
              import('@/features/work-history/pages/WorkHistoryDetailPage').then(m => ({
                Component: m.WorkHistoryDetailPage,
              })),
          },
        ],
      },
      {
        path: 'messaging',
        children: [
          {
            index: true,
            lazy: () =>
              import('@/features/im/pages/UnifiedInboxPage').then(m => ({
                Component: m.default,
              })),
          },
          {
            path: ':conversationId',
            lazy: () =>
              import('@/features/im/pages/UnifiedInboxPage').then(m => ({
                Component: m.default,
              })),
          },
          {
            path: 'settings',
            lazy: () =>
              import('@/features/im/pages/IMSettingsPage').then(m => ({
                Component: m.default,
              })),
          },
        ],
      },
      {
        path: 'support',
        children: [
          { index: true, element: <Navigate to="/support/new" replace /> },
          { path: 'new', lazy: () => import('@/features/support/ui/NewSupportSessionPage').then(m => ({ Component: m.NewSupportSessionPage })) },
          { path: 'sessions/:id', lazy: () => import('@/features/support/ui/SupportSessionPage').then(m => ({ Component: m.SupportSessionPage })) },
          { path: 'sessions/:id/history', lazy: () => import('@/features/support/ui/SessionHistoryDetailPage').then(m => ({ Component: m.SessionHistoryDetailPage })) },
          { path: 'history', lazy: () => import('@/features/support/ui/SupportHistoryPage').then(m => ({ Component: m.SupportHistoryPage })) },
          { path: 'kb', lazy: () => import('@/features/support/ui/KnowledgeBaseSearchPage').then(m => ({ Component: m.KnowledgeBaseSearchPage })) },
        ]
      },
      {
        path: 'workplace/hooks',
        lazy: () => import('@/features/workplace/pages/CustomHooksPage').then(m => ({ Component: m.CustomHooksPage })),
      },
      {
        path: 'workplace/kv-store',
        lazy: () => import('@/features/workplace/pages/WorkspaceKVStorePage').then(m => ({ Component: m.WorkspaceKVStorePage })),
      },
      {
        path: 'campaigns/planning',
        lazy: () => import('@/features/campaigns/pages/CampaignPlanningPage').then(m => ({ Component: m.CampaignPlanningPage })),
      },
    ],
  },

  // ─── Admin routes (admin role) ────────────────────────────────────────────
  {
    path: "/admin",
    element: (
      <ProtectedRoute requiredRole="admin">
        <AppLayout />
      </ProtectedRoute>
    ),
    errorElement: <ErrorPage />,
    children: [
      { index: true, element: <Navigate to="agents" replace /> },
      {
        // BUG-GOOG-010 FIX: /admin/system → AdminSystemPage
        // E2E test navigates to /admin/system and expects [data-testid="system-settings"].
        // implements: CRS-GOOG-010
        path: "system",
        lazy: () =>
          import("@/features/admin/pages/AdminSystemPage").then((m) => ({ Component: m.AdminSystemPage })),
      },
      {
        path: "mcp",
        element: <Navigate to="/admin/mcp-agents" replace />,
      },
      {
        path: "im",
        children: [
          {
            index: true,
            lazy: () =>
              import("@/features/im/pages/UnifiedInboxPage").then((m) => ({
                Component: m.default,
              })),
          },
          {
            path: ":conversationId",
            lazy: () =>
              import("@/features/im/pages/UnifiedInboxPage").then((m) => ({
                Component: m.default,
              })),
          },
        ],
      },
      {
        path: "privacy",
        element: <Navigate to="/admin/compliance" replace />,
      },
      {
        path: "mcp-servers",
        element: <Navigate to="/admin/proxy/mcp-servers" replace />,
      },
      {
        path: "observability",
        element: <Navigate to="/admin/proxy/observability" replace />,
      },
      {
        path: "integrations",
        element: <Navigate to="/admin/vnp-dba" replace />,
      },
      {
        path: "integrations/dba",
        element: <Navigate to="/admin/vnp-dba/instances" replace />,
      },
      {
        path: "branding",
        lazy: () => import("@/features/branding/pages/BrandingPage").then((m) => ({ Component: m.default })),
      },
      {
        path: "code-sandbox",
        lazy: () => import("@/features/code-sandbox/pages/CodeSandboxAdminPage").then((m) => ({ Component: m.default })),
      },
      {
        path: "plugins",
        lazy: () => import("@/features/plugins/pages/PluginsListPage").then((m) => ({ Component: m.default })),
      },
      {
        path: "plugins/:id",
        lazy: () => import("@/features/plugins/pages/PluginDetailPage").then((m) => ({ Component: m.default })),
      },
      {
        path: "plugins/:id/publish",
        lazy: () => import("@/features/plugins/pages/PluginDistributionPage").then((m) => ({ Component: m.default })),
      },
      {
        path: "workspace/presets",
        lazy: () => import("@/features/workspace-presets/pages/WorkspacePresetsPage").then((m) => ({ Component: m.default })),
      },
      {
        path: "kms",
        lazy: () => import("@/features/kms/pages/KMSAdminPage").then((m) => ({ Component: m.default })),
      },
      {
        path: "mcp/files",
        lazy: () => import("@/features/mcp-files/pages/MCPFilesAdminPage").then((m) => ({ Component: m.default })),
      },
      // [NEW — WS-031] Enterprise Org Workspace Governance
      {
        path: "workspaces",
        lazy: () =>
          import("@/ee/pages/admin/OrgWorkspacesPage").then((m) => ({
            Component: () => (
              <EEGate feature="workspace_governance">
                <m.OrgWorkspacesPage />
              </EEGate>
            ),
          })),
      },
      {
        path: "policies",
        lazy: () =>
          import("@/ee/pages/admin/PolicyManagementPage").then((m) => ({
            Component: () => (
              <EEGate feature="workspace_governance">
                <m.PolicyManagementPage />
              </EEGate>
            ),
          })),
      },
      {
        path: "audit",
        lazy: () =>
          import("@/ee/pages/admin/WorkspaceAuditDashboard").then((m) => ({
            Component: () => (
              <EEGate feature="workspace_governance">
                <m.WorkspaceAuditDashboard />
              </EEGate>
            ),
          })),
      },
      {
        path: "diagnostics",
        lazy: () =>
          import("@/features/diagnostics/pages/DiagnosticsDashboardPage").then((m) => ({ Component: m.default })),
      },
      {
        path: "env-management",
        lazy: () =>
          import("@/features/env-management/pages/EnvManagementPage").then((m) => ({ Component: m.default })),
      },
      {
        path: "arch-agents",
        lazy: () =>
          import("@/features/architecture/pages/WorkspaceAgentAdminPage").then((m) => ({ Component: m.WorkspaceAgentAdminPage })),
      },
      {
        path: "doc-agents",
        lazy: () =>
          import("@/features/doc-agent/pages/DocAgentAdminPage").then((m) => ({ Component: m.DocAgentAdminPage })),
      },
      {
        // Mail Agent Admin — TDD-UI-38 (v7)
        path: 'mail-agents',
        children: [
          {
            index: true,
            lazy: () =>
              import('@/features/mail/pages/MailAgentAdminPage').then(m => ({
                Component: m.MailAgentAdminPage,
              })),
          },
          {
            path: 'new',
            lazy: () =>
              import('@/features/mail/pages/MailAgentAdminPage').then(m => ({
                Component: m.MailAgentAdminPage,
              })),
          },
        ],
      },
      {
        // Products Agent (products-agent integration health/roles/audit) —
        // canonical surviving surface for what was investigated to be one
        // domain, not three (see comments on the "products" and "pikb"
        // routes below).
        path: "products-agents",
        children: [
          {
            index: true,
            lazy: () =>
              import("@/features/products/ui/PIKBAdminPage").then(m => ({
                Component: m.PIKBAdminPage,
              })),
          },
          {
            // Fixed: previously rendered the WHOLE PIKBAdminPanel (agents +
            // roles + audit) instead of just actor-role management implied
            // by the URL. PIKBActorRolesPage wraps PIKBActorRoleMatrix
            // standalone (it only needs `orgId`).
            path: "actor-roles",
            lazy: () =>
              import("@/features/products/ui/PIKBActorRolesPage").then(m => ({
                Component: m.PIKBActorRolesPage,
              })),
          },
          {
            // Personal "my activity" view (scoped to the current user), not
            // an admin-wide audit log — despite living under /admin/. See
            // ADMIN_ROUTES.PRODUCTS_AGENTS_MY_ACTIVITY.
            path: "audit",
            lazy: () =>
              import("@/features/products/ui/PIKBAuditHistoryView").then(m => ({
                Component: m.PIKBAuditHistoryView,
              })),
          },
        ],
      },
      // TASK-UI-PKB-010: PIKB Admin Products Page — redirects to the
      // canonical /admin/products-agents (same PIKBAdminPage component;
      // consolidated so there's a single admin entry point for
      // products-agent integration, not two URLs for one page).
      {
        path: "products",
        element: <Navigate to="/admin/products-agents" replace />,
      },
      // PIKB Admin UI (CR-PKB-WP-018) — actor-roles and audit are
      // near-duplicate rebuilds of /admin/products-agents/actor-roles and
      // /admin/products-agents/audit hitting the same backend APIs;
      // redirected to the canonical versions. audit-log is genuinely
      // different (hits /api/v1/admin/audit, richer schema) and stays live.
      {
        path: "pikb",
        children: [
          {
            path: "actor-roles",
            element: <Navigate to="/admin/products-agents/actor-roles" replace />,
          },
          {
            path: "audit",
            element: <Navigate to="/admin/products-agents/audit" replace />,
          },
          {
            // PKB-06: Dedicated audit log panel backed by GET /api/v1/admin/audit
            path: "audit-log",
            lazy: () => import("@/features/admin/pages/pikb/PIKBAuditLogPage").then((m) => ({ Component: m.PIKBAuditLogPage })),
          },
        ],
      },
      {
        // Switched to the richer refresh-pages implementation (search,
        // pagination, drag-to-reorder, share/feature/listed workflows,
        // Opal/CLAUDE.md-compliant) — pages/admin/AgentAdminPage.tsx (the
        // old plain CSS-module stub, with dead Create/Edit links pointing
        // at unregistered /admin/agents/create /admin/agents/:id/edit
        // routes) is deleted.
        path: "agents",
        lazy: () =>
          import("@/features/admin/pages/refresh-pages/AgentsPage").then((m) => ({ Component: m.default })),
      },
      {
        path: "mcp-agents",
        lazy: () =>
          import("@/features/admin/pages/mcp-agents/AgentListPage").then((m) => ({ Component: m.AgentListPage })),
      },
      {
        path: "mcp-agents/add",
        lazy: () =>
          import("@/features/admin/pages/mcp-agents/AddAgentWizard").then((m) => ({ Component: m.AddAgentWizard })),
      },
      {
        path: "mcp-agents/tools",
        lazy: () =>
          import("@/features/admin/pages/mcp-agents/ToolExplorerPage").then((m) => ({ Component: m.ToolExplorerPage })),
      },
      {
        path: "mcp-agents/logs",
        lazy: () =>
          import("@/features/admin/pages/mcp-agents/AuditLogsPage").then((m) => ({ Component: m.AuditLogsPage })),
      },
      {
        // CR-AGENTSCHED-WP-002 — separate from ToolExplorerPage (whose
        // Invoke Tool button stays intentionally disabled, untouched by
        // this route/page).
        path: "mcp-agents/adhoc-invoke",
        lazy: () =>
          import("@/features/admin/pages/mcp-agents/AdhocInvokePage").then((m) => ({ Component: m.AdhocInvokePage })),
      },
      {
        path: "mcp-agents/:id",
        lazy: () =>
          import("@/features/admin/pages/mcp-agents/AgentDetailPage").then((m) => ({ Component: m.AgentDetailPage })),
      },
      // ═══════════════════════════════════════════════════════════════
      // Actions routes — MCP-02/MCP-03 & OpenAPI Actions
      // AdminSidebar.tsx had sidebar entries for both routes but the
      // routes themselves were never registered here, causing 404 on click.
      // ═══════════════════════════════════════════════════════════════
      {
        path: "actions",
        children: [
          {
            // MCP-03: Consume External MCP Servers — Add/Auth/Fetch tools
            // Source: sections/actions/MCPPageContent.tsx
            path: "mcp",
            lazy: () =>
              import("@/shared/ui/sections/actions/MCPPageContent").then((m) => ({ Component: m.default })),
          },
          {
            // OpenAPI Actions — Custom OpenAPI tool definitions
            // Source: sections/actions/OpenApiPageContent.tsx
            path: "open-api",
            lazy: () =>
              import("@/shared/ui/sections/actions/OpenApiPageContent").then((m) => ({ Component: m.default })),
          },
        ],
      },
      {
        // BUG-DEV002-002 — per-tool MCP access rule overview (ROUTES.ADMIN_TOOL_PERMISSIONS)
        path: "tool-permissions",
        lazy: () =>
          import("@/features/admin/pages/mcp-tools/ToolPermissionsPage").then((m) => ({ Component: m.default })),
      },
      {
        // TASK-ADG-001 (SOL-UI-ADG-001): Tool Governance Admin Center (5 tabs)
        path: "tools",
        lazy: () =>
          import("@/features/admin-tool-governance/components/ToolGovernanceCenter").then((m) => ({
            Component: m.ToolGovernanceCenter,
          })),
      },
      {
        // Alias for /admin/tools/catalog -> ToolGovernanceCenter
        path: "tools/catalog",
        lazy: () =>
          import("@/features/admin-tool-governance/components/ToolGovernanceCenter").then((m) => ({
            Component: m.ToolGovernanceCenter,
          })),
      },
      {
        // Desktop Agent tool management — /admin/desktop-agent/tools
        path: "desktop-agent/tools",
        lazy: () =>
          import("@/features/admin/pages/DesktopAgentToolsPage").then((m) => ({ Component: m.DesktopAgentToolsPage })),
      },
      {
        path: "openwork",
        lazy: () =>
          import("@/features/admin/pages/openwork/OpenWorkAdminPage").then((m) => ({ Component: m.OpenWorkAdminPage })),
      },
      {
        path: "openwork/config",
        lazy: () =>
          import("@/features/admin/pages/openwork/OpenWorkOrgConfigPage").then((m) => ({ Component: m.OpenWorkOrgConfigPage })),
      },
      {
        path: "git-management",
        lazy: () =>
          import("@/features/admin/pages/git-management/GitAdminDashboard").then((m) => ({ Component: m.GitAdminDashboard })),
      },
      {
        path: "system-integrations",
        lazy: () =>
          import("@/features/system-integrations/pages/SystemAgentsAdminPage").then((m) => ({ Component: m.SystemAgentsAdminPage })),
      },
      {
        // CR-040 (SOL-040) — Runtime Agent admin (provision/monitor/revoke).
        // Named "Runtime Agents" (not "Agents") to avoid confusion with the
        // "agents" route above (AI persona).
        path: "runtime-agents",
        lazy: () =>
          import("@/features/admin/pages/refresh-pages/RuntimeAgentsPage").then((m) => ({
            Component: m.default,
          })),
      },
      {
        // TASK-ADM-001 (SOL-UI-ADM-001): Admin AI Sessions Observability Dashboard
        path: "ai-agents",
        lazy: () =>
          import("@/features/admin-ai-agents/components/AdminAiAgentsPage").then((m) => ({
            Component: m.AdminAiAgentsPage,
          })),
      },
      {
        path: "runtime-agents/new",
        lazy: () =>
          import("@/features/admin/pages/refresh-pages/RuntimeAgentProvisionPage").then((m) => ({
            Component: m.default,
          })),
      },
      {
        path: "runtime-agents/:id",
        lazy: () =>
          import("@/features/admin/pages/refresh-pages/RuntimeAgentDetailPage").then((m) => ({
            Component: m.default,
          })),
      },
      {
        path: "connectors",
        lazy: () =>
          import("@/features/admin/pages/ConnectorsPage").then((m) => ({ Component: m.ConnectorsPage })),
      },
      {
        path: "connectors/new",
        lazy: () =>
          import("@/features/admin/pages/ConnectorSetupPage").then((m) => ({
            Component: m.ConnectorSetupPage,
          })),
      },
      {
        // CR-004 — 7-step Google Sheets connector setup wizard, sibling route
        // to "connectors/new" (generic 2-step flow) — does NOT replace it.
        path: "connectors/new/gsheet",
        lazy: () =>
          import("@/features/admin/pages/connectors/gsheet/ConnectorSetupWizardPage").then((m) => ({
            Component: m.ConnectorSetupWizardPage,
          })),
      },
      {
        path: "connectors/:id",
        lazy: () =>
          import("@/features/admin/pages/ConnectorDetailPage").then((m) => ({
            Component: m.ConnectorDetailPage,
          })),
      },
      {
        // CR-006 — Connector Hub (multi-source registry grid).
        path: "connectors/hub",
        lazy: () =>
          import("@/features/admin/pages/ConnectorHubPage").then((m) => ({
            Component: m.ConnectorHubPage,
          })),
      },
      {
        // CR-015 — Bot Integration (Slack/Discord) admin config.
        path: "bots",
        lazy: () =>
          import("@/features/admin/pages/SlackBotPage").then((m) => ({ Component: m.SlackBotPage })),
      },
      {
        path: "discord-bot",
        lazy: () =>
          import("@/features/admin/pages/DiscordBotPage").then((m) => ({ Component: m.DiscordBotPage })),
      },
      {
        path: "sync-monitor",
        lazy: () =>
          import("@/features/admin/pages/SyncMonitorPage").then((m) => ({ Component: m.SyncMonitorPage })),
      },
      {
        path: "users",
        lazy: () =>
          import("@/features/admin/pages/UsersPage").then((m) => ({ Component: m.UsersPage })),
      },
      {
        path: "groups",
        lazy: () =>
          import("@/features/admin/pages/GroupsPage").then((m) => ({ Component: m.GroupsPage })),
      },
      {
        // Sub-routes for the real Groups CRUD flow (previously the "groups"
        // route above pointed at a bare placeholder stub with no way to
        // create/edit a group at all).
        path: "groups/create",
        lazy: () =>
          import("@/features/admin/pages/refresh-pages/GroupsPage/CreateGroupPage").then((m) => ({
            Component: m.default,
          })),
      },
      {
        path: "groups/:id",
        lazy: () =>
          import("@/features/admin/pages/EditGroupPage").then((m) => ({
            Component: m.EditGroupPageRoute,
          })),
      },
      {
        path: "service-accounts",
        lazy: () =>
          import("@/features/admin/pages/ServiceAccountsPage").then((m) => ({
            Component: m.ServiceAccountsPage,
          })),
      },
      {
        // CR-020 — was a dead link (ADMIN_ROUTES.SCIM/ScimCard both already
        // pointed here; only the route registration was missing).
        path: "scim",
        lazy: () =>
          import("@/features/admin/pages/ScimPage").then((m) => ({ Component: m.ScimPage })),
      },
      {
        // TASK-SA003-001-5 (BUG-SA003-001) — SSO provider configuration
        // (OIDC/SAML) admin page, same gap pattern as "scim" above.
        path: "sso",
        lazy: () =>
          import("@/features/admin/pages/SSOPage").then((m) => ({ Component: m.SSOPage })),
      },
      {
        path: "token-rate-limits",
        lazy: () =>
          import("@/features/admin/pages/TokenRateLimitsPage").then((m) => ({
            Component: m.TokenRateLimitsPage,
          })),
      },
      {
        // CR-017 — Google API Quota Management. GoogleQuotaPage.tsx uses
        // `export default`, not a named export — matches the
        // ImageGenerationPage precedent above, use `m.default`.
        path: "google-quota",
        lazy: () =>
          import("@/features/admin/pages/refresh-pages/GoogleQuotaPage").then((m) => ({
            Component: m.default,
          })),
      },
      // BUG-GOOG-004 FIX: Drive Watch Channels admin page
      // implements: CRS-GOOG-004
      {
        path: "drive-watch-channels",
        lazy: () =>
          import("@/features/admin/pages/DriveWatchChannelsPage").then((m) => ({
            Component: m.DriveWatchChannelsPage,
          })),
      },
      // BUG-GOOG-005 FIX: Schema Change Manager admin page
      // implements: CRS-GOOG-005
      {
        path: "schema",
        lazy: () =>
          import("@/features/admin/pages/connectors/gsheet/SchemaManagerPage").then((m) => ({
            Component: m.SchemaManagerPage,
          })),
      },
      {
        // CR-037 — was pointing at `@/features/admin/pages/LLMConfigPage`, an old
        // single-form implementation whose backend persistence is a TODO
        // stub (config didn't survive reload). The real 12-modal
        // implementation (dispatch via `getProvider()` in
        // `@/shared/lib/llmConfig`) lives in `LLMConfigurationPage` but was never
        // wired to any route (BUG-API-UI5-020). `LLMConfigPage.tsx` is left
        // in place (not deleted) for fast rollback.
        path: "configuration/language-models",
        lazy: () =>
          import("@/features/admin/pages/LLMConfigPage").then((m) => ({
            Component: m.LLMConfigPage,
          })),
      },
      {
        // CR-034 — Admin Chat Preferences. Component tồn tại đầy đủ nhưng chưa
        // từng được đăng ký route (BUG-API-UI5-020).
        path: "configuration/chat-preferences",
        lazy: () =>
          import("@/features/admin/pages/refresh-pages/ChatPreferencesPage").then((m) => ({
            Component: m.default,
          })),
      },
      {
        // CR-025 — ImageGenerationPage/index.tsx uses `export default`, NOT a
        // named export (e.g. `AgentAdminPage`/`ConnectorsPage` above use named
        // exports). Use `m.default` here, do not copy the named-export
        // pattern or lazy-load crashes with "Component is not a function".
        path: "configuration/image-generation",
        lazy: () =>
          import("@/features/admin/pages/refresh-pages/ImageGenerationPage").then((m) => ({
            Component: m.default,
          })),
      },
      {
        // CR-024 — Voice Mode TTS/STT admin configuration (connect/disconnect
        // STT/TTS providers). VoiceConfigurationPage.tsx uses `export
        // default`, NOT a named export — same pattern as ImageGenerationPage
        // above, use `m.default`. This route was the sole remaining FE gap
        // blocking CR-024 manual E2E (TASK-CR-024-006): the page existed but
        // was never mounted anywhere.
        path: "configuration/voice",
        lazy: () =>
          import("@/features/admin/pages/refresh-pages/VoiceConfigurationPage").then((m) => ({
            Component: m.default,
          })),
      },
      {
        // Page existed, was never registered as a route at all. Backend is
        // still hardcoded stubs (health/status always report disconnected,
        // PUT doesn't persist) — page loads fine, toggle/reconnect no-op.
        path: "configuration/code-interpreter",
        lazy: () =>
          import("@/features/admin/pages/refresh-pages/CodeInterpreterPage").then((m) => ({
            Component: m.default,
          })),
      },
      {
        // Page existed, was never registered as a route at all. Backend only
        // implements the two GET list endpoints (both empty-stub); every
        // connect/disconnect/test/activate action 404s until that's built out.
        path: "configuration/web-search",
        lazy: () =>
          import("@/features/admin/pages/refresh-pages/WebSearchPage").then((m) => ({
            Component: m.default,
          })),
      },
      {
        path: "embeddings",
        lazy: () =>
          import("@/features/admin/pages/EmbeddingsPage").then((m) => ({ Component: m.EmbeddingsPage })),
      },
      {
        path: "indexing",
        lazy: () =>
          import("@/features/admin/pages/EmbeddingsPage").then((m) => ({ Component: m.EmbeddingsPage })),
      },
      {
        path: "billing",
        lazy: () =>
          import("@/features/admin/pages/BillingPage").then((m) => ({ Component: m.BillingPage })),
      },
      {
        path: "theme",
        lazy: () =>
          import("@/ee/refresh-pages/admin/ThemePage").then((m) => ({
            Component: m.default,
          })),
      },
      {
        // TASK-DOCGOV-023 (docs/crs/v8/doc-governance/CR-DOCGOV-WP-004) —
        // admin surface for mcp-proxy's approval gate (SOL-DOCGOV-004).
        path: "tool-approvals",
        lazy: () =>
          import("@/features/admin/pages/ToolApprovalsPage").then((m) => ({
            Component: m.ToolApprovalsPage,
          })),
      },
      {
        // BUG-GOOG3-003 FIX: /admin/approvals — Agent Registry approval queue.
        // E2E tests PERM-TC-015-pos and PERM-TC-022-neg expect data-testid="approval-queue".
        // ProtectedRoute requiredRole="admin" is inherited from the /admin parent (line 1115).
        path: "approvals",
        lazy: () =>
          import("@/features/admin/pages/AdminApprovalsPage").then((m) => ({
            Component: m.AdminApprovalsPage,
          })),
      },
      {
        // TASK-DOCAGENT-011 (docs/crs/v8/doc-agent) — admin CRUD for
        // mcp-proxy's agent_peer_credential/agent_peer_grant tables.
        path: "agent-peer-grants",
        lazy: () =>
          import("@/features/admin/pages/AgentPeerGrantsPage").then((m) => ({
            Component: m.AgentPeerGrantsPage,
          })),
      },
      {
        // TASK-UI-SKILLS-012 (docs/crs/v9/skills-management) — admin bulk
        // import of skills from a server-side folder. Phase 1: folder path
        // only. Phase 2 (file upload UI) deferred.
        path: "skills/import",
        lazy: () =>
          import("@/features/skills/pages/SkillImportPage").then((m) => ({
            Component: m.SkillImportPage,
          })),
      },
      {
        path: "documents",
        lazy: () =>
          import("@/features/admin/pages/DocumentsPage").then((m) => ({ Component: m.DocumentsPage })),
      },
      {
        // TASK-AB003-003 (BUG-AB003-003) — Document Sets admin CRUD, mirrors
        // the groups/groups-create/groups/:id route triple above. Path
        // nested under "documents" to distinguish from raw file management
        // (DocumentsPage) — see TASK-AB003-003 "Rủi ro" for the still-open
        // nav-placement question (no dedicated tab/link wired yet).
        path: "documents/sets",
        lazy: () =>
          import("@/features/admin/pages/refresh-pages/DocumentSetsPage").then((m) => ({
            Component: m.default,
          })),
      },
      {
        path: "documents/sets/create",
        lazy: () =>
          import("@/features/admin/pages/refresh-pages/DocumentSetsPage/CreateDocumentSetPage").then(
            (m) => ({ Component: m.default })
          ),
      },
      {
        path: "documents/sets/:id",
        lazy: () =>
          import("@/features/admin/pages/EditDocumentSetPage").then((m) => ({
            Component: m.EditDocumentSetPageRoute,
          })),
      },
      {
        // CR-018 — Analytics & Usage Dashboard.
        path: "analytics",
        lazy: () =>
          import("@/features/admin/pages/AnalyticsPage").then((m) => ({ Component: m.AnalyticsPage })),
      },
      {
        // Alias — cùng component với "analytics", khớp ADMIN_ROUTES.USAGE.path
        // đã định nghĩa sẵn ở lib/admin-routes.ts (cây Opal chưa mount).
        path: "performance/usage",
        lazy: () =>
          import("@/features/admin/pages/AnalyticsPage").then((m) => ({ Component: m.AnalyticsPage })),
      },
      {
        path: "performance/query-history",
        lazy: () =>
          import("@/features/admin/pages/QueryHistoryPage").then((m) => ({ Component: m.QueryHistoryPage })),
      },
      {
        // CR-014 — Knowledge Graph Explorer: cấu hình extraction.
        path: "kg",
        lazy: () =>
          import("@/features/admin/pages/KGConfigPage").then((m) => ({ Component: m.KGConfigPage })),
      },
      {
        // CR-016 — Audit Log Viewer.
        path: "audit-log",
        lazy: () =>
          import("@/features/admin/pages/AuditLogPage").then((m) => ({ Component: m.AuditLogPage })),
      },
      {
        // UI-SOL-PA-008 — Provider Agent fleet dashboard / selection policy /
        // audit log. Components existed since 2026-08-31 (sections/agent/
        // ProviderAgentDashboard.tsx, sections/agent/PolicyConfigPanel.tsx,
        // sections/admin/AuditLogViewer.tsx) but had no route at all.
        path: "provider-agents",
        lazy: () =>
          import("@/features/admin/pages/ProviderAgentAdminPage").then((m) => ({
            Component: m.ProviderAgentAdminPage,
          })),
      },
      {
        // CR-041 (SOL-041) — Session & Key Security admin.
        path: "security/sessions",
        lazy: () =>
          import("@/features/admin/pages/refresh-pages/ActiveSessionsPage").then((m) => ({
            Component: m.default,
          })),
      },
      {
        path: "security/key-audit-log",
        lazy: () =>
          import("@/features/admin/pages/refresh-pages/KeyAuditLogPage").then((m) => ({
            Component: m.default,
          })),
      },
      {
        // CR-044 (SOL-044 wiring delta, 2026-09-02) — Prompt Firewall / DLP
        // stats dashboard, same "admin/security/*" family as the 2 routes above.
        path: "security/prompt-firewall",
        lazy: () =>
          import("@/features/admin/pages/refresh-pages/PromptFirewallStatsPage").then((m) => ({
            Component: m.default,
          })),
      },
      {
        // CR-042 (SOL-042 wiring delta, 2026-09-02) — BYOK admin panel.
        path: "privacy/byok",
        lazy: () =>
          import("@/features/admin/pages/refresh-pages/BYOKSettingsPage").then((m) => ({
            Component: m.default,
          })),
      },
      {
        // UI-SOL-PE-016 (v6/privacy-enhance ZKPA, Sprint 8) — Anonymization
        // Pipeline admin (Emergency Gate + DLP policies). See
        // AnonymizationAdminPage.tsx for backend-readiness caveats.
        path: "privacy/anonymization",
        lazy: () =>
          import("@/features/admin/pages/AnonymizationAdminPage").then((m) => ({
            Component: m.AnonymizationAdminPage,
          })),
      },
      {
        // UI-SOL-PE-016 (v6/privacy-enhance ZKPA, Sprint 8) — Differential
        // Privacy budget admin. See PrivacyBudgetAdminPage.tsx for
        // backend-readiness caveats.
        path: "privacy/dp-budget",
        lazy: () =>
          import("@/features/admin/pages/PrivacyBudgetAdminPage").then((m) => ({
            Component: m.PrivacyBudgetAdminPage,
          })),
      },
      {
        // CU-006-2 — CUA Observability admin page: metrics summary + paginated
        // audit log. Route: /admin/cua-observability
        path: "cua-observability",
        lazy: () =>
          import("@/features/admin/pages/CUAObservabilityPage").then((m) => ({
            Component: m.default,
          })),
      },
      {
        // CR-019 — Multi-Tenant Admin Panel. Independent files (not a
        // barrel re-export), matching ConnectorSetupPage/ConnectorDetailPage
        // pattern — see SOL-019 §3.2.
        path: "tenants",
        lazy: () =>
          import("@/features/admin/pages/TenantsPage").then((m) => ({ Component: m.TenantsPage })),
      },
      {
        path: "tenants/:id",
        lazy: () =>
          import("@/features/admin/pages/TenantDetailPage").then((m) => ({
            Component: m.TenantDetailPage,
          })),
      },
      {
        // CU-011-2 — Admin CUA Quota page: per-org quota config + usage dashboard.
        // Route: /admin/orgs/:orgId/cua-quota
        path: "orgs/:orgId/cua-quota",
        lazy: () =>
          import("@/features/admin/computer-use/pages/CUAAdminQuotaPage").then((m) => ({
            Component: m.CUAAdminQuotaPage,
          })),
      },
      {
        // CR-036 — Standard Answers (EE): keyword/regex -> answer FAQ pairs,
        // matched by AI Service before the LLM call (SOL-036 §3.4/§3.8).
        path: "standard-answer",
        lazy: () =>
          import("@/features/admin/pages/StandardAnswersPage").then((m) => ({
            Component: m.StandardAnswersPage,
          })),
      },
      {
        // Hook Extensions (EE). Page existed, was never registered as a
        // route at all — also had a compile-breaking undefined `router`
        // reference (fixed, see HooksPage/index.tsx). EE/enabled gating is
        // self-contained inside the page (redirects to "/" if not
        // applicable), same convention as ScimPage — no router-level guard
        // needed. Backend hooks CRUD is still largely unimplemented
        // (only GET /admin/hooks/specs exists, wrong response shape) — page
        // loads and self-gates correctly, but hook management itself won't
        // work until that's built out.
        path: "hooks",
        lazy: () =>
          import("@/ee/refresh-pages/admin/HooksPage").then((m) => ({
            Component: m.default,
          })),
      },
      {
        path: 'customer-care',
        children: [
          { index: true, lazy: () => import('@/features/admin/ui/customer-care/CustomerCareAdminPage').then(m => ({ Component: m.CustomerCareAdminPage })) },
          { path: 'products/:productId', lazy: () => import('@/features/admin/ui/customer-care/ProductDetailPage').then(m => ({ Component: m.ProductDetailPage })) },
          { path: 'systems/:systemId', lazy: () => import('@/features/admin/ui/customer-care/SystemDetailPage').then(m => ({ Component: m.SystemDetailPage })) },
          { path: 'systems/:systemId/agent', lazy: () => import('@/features/admin/ui/customer-care/SystemAgentSetupPage').then(m => ({ Component: m.SystemAgentSetupPage })) },
        ]
      },
      {
        path: 'mcp-remote',
        lazy: () => import('@/features/settings/pages/MCPRemoteControlPage').then(m => ({ Component: m.MCPRemoteControlPage })),
      },
      {
        path: 'memory-gdpr',
        lazy: () => import('@/features/memory/pages/MemoryGDPRPage').then(m => ({ Component: m.MemoryGDPRPage })),
      },
      {
        // Memory-group admin (member list / invite / key-rotation). Was a
        // dead-end route with no `:id` param and no nav entry — GroupAdminPage
        // hard-requires a group id via useParams and can't render standalone.
        // Index reuses GroupMemoryPage (the existing "my groups" list, backed
        // by the working GET /memory/groups endpoint) as the entry point;
        // its row-select now navigates to the detail route below instead of
        // the never-registered /memory/groups/:id.
        path: 'group-admin',
        children: [
          {
            index: true,
            lazy: () =>
              import('@/features/memory/pages/GroupMemoryPage').then(m => ({ Component: m.GroupMemoryPage })),
          },
          {
            path: ':id',
            lazy: () =>
              import('@/features/memory/pages/GroupAdminPage').then(m => ({ Component: m.GroupAdminPage })),
          },
        ],
      },
      {
        path: 'pikb-integration',
        lazy: () => import('@/features/system-integrations/pages/PIKBIntegrationPage').then(m => ({ Component: m.PIKBIntegrationPage })),
      },
      // ═══════════════════════════════════════════════════════════════
      // Doc Agent Admin Routes
      // ═══════════════════════════════════════════════════════════════
      {
        path: 'doc-agents',
        lazy: () =>
          import('@/features/doc-agent/pages/DocAgentAdminPage').then(m => ({
            Component: m.DocAgentAdminPage,
          })),
      },
      // ═══════════════════════════════════════════════════════════════
      // Instance Message Admin Routes
      // ═══════════════════════════════════════════════════════════════
      {
        path: 'im-agents',
        lazy: () =>
          import('@/features/im/pages/IMAgentAdminPage').then(m => ({
            Component: m.default,
          })),
      },
      {
        // IM-07: Compliance audit page — GET /api/v1/admin/im-agents/audit-events
        path: 'im-audit',
        lazy: () =>
          import('@/features/im/pages/IMAuditPage').then(m => ({
            Component: m.IMAuditPage,
          })),
      },
      // ═══════════════════════════════════════════════════════════════
      // Buzz Admin Routes — TDD-UI-12 (CR-BZ-05..07)
      // Pages: relay config, member provisioning, audit chain viewer
      // ═══════════════════════════════════════════════════════════════
      {
        path: 'buzz',
        children: [
          {
            index: true,
            element: <Navigate to="relays" replace />,
          },
          {
            path: 'relays',
            lazy: () =>
              import('@/features/admin/pages/buzz/relays/BuzzRelayListPage').then(m => ({ Component: m.BuzzRelayListPage })),
          },
          {
            path: 'members',
            lazy: () =>
              import('@/features/admin/pages/buzz/members/BuzzMembersPage').then(m => ({ Component: m.BuzzMembersPage })),
          },
          {
            path: 'audit',
            lazy: () =>
              import('@/features/admin/pages/buzz/audit/BuzzAuditPage').then(m => ({ Component: m.BuzzAuditPage })),
          },
        ],
      },
      // ═══════════════════════════════════════════════════════════════
      // VNP-Policy Admin Routes — TASK-UI-POL-011 (v8/vnp-policy)
      // Sprint 3 wiring: dashboard, list, create, eval, compliance,
      // conflicts, enforcement
      // ═══════════════════════════════════════════════════════════════
      {
        path: 'policy',
        children: [
          {
            index: true,
            lazy: () =>
              import('@/features/admin/pages/policy/PolicyDashboardPage').then(m => ({ Component: m.PolicyDashboardPage })),
          },
          {
            path: 'policies',
            lazy: () =>
              import('@/features/admin/pages/policy/PolicyListPage').then(m => ({ Component: m.PolicyListPage })),
          },
          {
            path: 'policies/new',
            lazy: () =>
              import('@/features/admin/pages/policy/CreatePolicyPage').then(m => ({ Component: m.CreatePolicyPage })),
          },
          {
            path: 'evaluation',
            lazy: () =>
              import('@/features/admin/pages/policy/PolicyEvaluationPage').then(m => ({ Component: m.PolicyEvaluationPage })),
          },
          {
            path: 'compliance',
            lazy: () =>
              import('@/features/admin/pages/policy/ComplianceDashboardPage').then(m => ({ Component: m.ComplianceDashboardPage })),
          },
          {
            path: 'conflicts',
            lazy: () =>
              import('@/features/admin/pages/policy/ConflictDetectionPage').then(m => ({ Component: m.ConflictDetectionPage })),
          },
          {
            path: 'enforcement',
            lazy: () =>
              import('@/features/admin/pages/policy/EnforcementPipelinePage').then(m => ({ Component: m.EnforcementPipelinePage })),
          },
        ],
      },
      // ═══════════════════════════════════════════════════════════════
      // VNP-Proxy Admin Routes — TASK-UI-PRX-011 (v8/vnp-proxy)
      // Sprint 3 wiring: dashboard, providers, virtual-keys,
      // observability, mcp-servers, fleet, security-policy
      // ═══════════════════════════════════════════════════════════════
      {
        path: 'proxy',
        children: [
          {
            index: true,
            lazy: () =>
              import('@/features/admin/pages/proxy/ProxyDashboardPage').then(m => ({ Component: m.ProxyDashboardPage })),
          },
          {
            path: 'providers',
            lazy: () =>
              import('@/features/admin/pages/proxy/ProviderListPage').then(m => ({ Component: m.ProviderListPage })),
          },
          {
            path: 'virtual-keys',
            lazy: () =>
              import('@/features/admin/pages/proxy/VirtualKeyManagementPage').then(m => ({ Component: m.VirtualKeyManagementPage })),
          },
          {
            path: 'observability',
            lazy: () =>
              import('@/features/admin/pages/proxy/ObservabilityPage').then(m => ({ Component: m.ObservabilityPage })),
          },
          {
            path: 'mcp-servers',
            lazy: () =>
              import('@/features/admin/pages/proxy/MCPServerRegistryPage').then(m => ({ Component: m.MCPServerRegistryPage })),
          },
          {
            path: 'fleet',
            lazy: () =>
              import('@/features/admin/pages/proxy/FleetSecurityPages').then(m => ({ Component: m.FleetManagementPage })),
          },
          {
            path: 'security',
            lazy: () =>
              import('@/features/admin/pages/proxy/FleetSecurityPages').then(m => ({ Component: m.SecurityPolicyPage })),
          },
        ],
      },
      // ═══════════════════════════════════════════════════════════════
      // VNP-Security Admin Routes — TASK-UI-SEC-011 (v8/vnp-security)
      // Sprint 3 wiring: dashboard, cve, findings, scan, reports, audit
      // ═══════════════════════════════════════════════════════════════
      {
        path: 'vnp-security',
        children: [
          {
            index: true,
            lazy: () =>
              import('@/features/admin/pages/security/SecurityDashboardPage').then(m => ({ Component: m.SecurityDashboardPage })),
          },
          {
            path: 'cve',
            lazy: () =>
              import('@/features/admin/pages/security/CVEIntelligencePage').then(m => ({ Component: m.CVEIntelligencePage })),
          },
          {
            path: 'findings',
            lazy: () =>
              import('@/features/admin/pages/security/FindingManagementPage').then(m => ({ Component: m.FindingManagementPage })),
          },
          {
            path: 'scan',
            lazy: () =>
              import('@/features/admin/pages/security/ScanManagementPage').then(m => ({ Component: m.ScanManagementPage })),
          },
          {
            path: 'reports',
            lazy: () =>
              import('@/features/admin/pages/security/ReportingAuditPages').then(m => ({ Component: m.ReportingPage })),
          },
          {
            path: 'audit',
            lazy: () =>
              import('@/features/admin/pages/security/ReportingAuditPages').then(m => ({ Component: m.AuditTrailPage })),
          },
        ],
      },
      // ═══════════════════════════════════════════════════════════════
      // VNP-DBA Admin Routes — TASK-UI-DBA-012 (v12 Wave 6)
      // ═══════════════════════════════════════════════════════════════
      {
        path: 'vnp-dba',
        children: [
          {
            index: true,
            lazy: () =>
              import('@/features/admin/pages/dba/DBADashboardPage').then(m => ({ Component: m.DBADashboardPage })),
          },
          {
            path: 'instances',
            lazy: () =>
              import('@/features/admin/pages/dba/InstanceListPage').then(m => ({ Component: m.InstanceListPage })),
          },
          {
            path: 'changes',
            lazy: () =>
              import('@/features/admin/pages/dba/ChangeDashboardPage').then(m => ({ Component: m.ChangeDashboardPage })),
          },
          {
            path: 'sql-editor',
            lazy: () =>
              import('@/features/admin/pages/dba/SQLEditorPage').then(m => ({ Component: m.SQLEditorPage })),
          },
          {
            path: 'policies',
            lazy: () =>
              import('@/features/admin/pages/dba/DatabasePoliciesPage').then(m => ({ Component: m.DatabasePoliciesPage })),
          },
          {
            path: 'audit',
            lazy: () =>
              import('@/features/admin/pages/dba/AuditLogPage').then(m => ({ Component: m.AuditLogPage })),
          },
        ],
      },
      // ═══════════════════════════════════════════════════════════════
      // OpenPanel Analytics Admin Routes — TASK-UI-OP-005 (v12 Wave 6)
      // ═══════════════════════════════════════════════════════════════
      {
        path: 'openpanel',
        children: [
          {
            index: true,
            lazy: () =>
              import('@/features/admin/pages/analytics/AnalyticsDashboardPage').then(m => ({ Component: m.AnalyticsDashboardPage })),
          },
          {
            path: 'settings',
            lazy: () =>
              import('@/features/admin/pages/analytics/AnalyticsSettingsPage').then(m => ({ Component: m.AnalyticsSettingsPage })),
          },
        ],
      },
      // ═══════════════════════════════════════════════════════════════
      // Distributed Search Admin Routes — TASK-UI-DSEARCH-ROUTER (v9)
      // ═══════════════════════════════════════════════════════════════
      {
        path: "distributed-search",
        lazy: () =>
          import("@/features/distributed-search/admin/pages/DistributedSearchAdminPage").then((m) => ({
            Component: m.DistributedSearchAdminPage,
          })),
      },
      {
        path: "distributed-search/embedding",
        lazy: () =>
          import("@/features/distributed-search/admin/pages/EmbeddingProfileAdminPage").then((m) => ({
            Component: m.EmbeddingProfileAdminPage,
          })),
      },
      // ═══════════════════════════════════════════════════════════════
      // Agent Registry Admin Routes — TASK-UI-AR-016 (v9/agent-registry)
      // /admin/agents/registry  (list + policies + detail)
      // ═══════════════════════════════════════════════════════════════
      {
        path: "agents/registry",
        lazy: () =>
          import("@/features/agent-registry/pages/AgentRegistryPage").then((m) => ({
            Component: m.AgentRegistryPage,
          })),
      },
      {
        path: "agents/registry/policies",
        lazy: () =>
          import("@/features/agent-registry/pages/PoliciesPage").then((m) => ({
            Component: m.PoliciesPage,
          })),
      },
      {
        path: "agents/registry/security",
        lazy: () =>
          import("@/features/agent-registry/pages/SecurityAuditDashboardPage").then((m) => ({
            Component: m.SecurityAuditDashboardPage,
          })),
      },
      {
        path: "agents/registry/:id",
        lazy: () =>
          import("@/features/agent-registry/pages/AgentDetailPage").then((m) => ({
            Component: m.AgentDetailPage,
          })),
      },
      // ── Alias: /admin/registry/agents → /admin/agents/registry ──────────
      // The URL /admin/registry/agents is the canonical URL shown in docs
      // and nav; we redirect all sub-paths to the real route above.
      {
        path: "registry/agents",
        lazy: () =>
          import("@/features/agent-registry/pages/AgentRegistryPage").then((m) => ({
            Component: m.AgentRegistryPage,
          })),
      },
      {
        path: "registry/agents/policies",
        lazy: () =>
          import("@/features/agent-registry/pages/PoliciesPage").then((m) => ({
            Component: m.PoliciesPage,
          })),
      },
      {
        path: "registry/agents/security",
        lazy: () =>
          import("@/features/agent-registry/pages/SecurityAuditDashboardPage").then((m) => ({
            Component: m.SecurityAuditDashboardPage,
          })),
      },
      {
        path: "registry/agents/:id",
        lazy: () =>
          import("@/features/agent-registry/pages/AgentDetailPage").then((m) => ({
            Component: m.AgentDetailPage,
          })),
      },
      // ═══════════════════════════════════════════════════════════════
      // Group 3 Admin Routes (v11)
      // ═══════════════════════════════════════════════════════════════
      {
        path: "diagnostics",
        lazy: () =>
          import("@/features/admin/pages/diagnostics/DiagnosticsDashboardPage").then((m) => ({
            Component: m.default,
          })),
      },
      {
        path: "plugins",
        lazy: () =>
          import("@/features/admin/pages/plugins/PluginListPage").then((m) => ({
            Component: m.default,
          })),
      },
      {
        path: "kms",
        lazy: () =>
          import("@/features/admin/pages/kms/KMSAdminPage").then((m) => ({
            Component: m.default,
          })),
      },
      {
        path: "env-management",
        lazy: () =>
          import("@/features/admin/pages/env-management/EnvManagementPage").then((m) => ({
            Component: m.default,
          })),
      },
      {
        path: "workspace-presets",
        lazy: () =>
          import("@/features/admin/pages/workspace-presets/WorkspacePresetsPage").then((m) => ({
            Component: m.default,
          })),
      },
      {
        path: "branding",
        lazy: () =>
          import("@/features/admin/pages/branding/BrandingPage").then((m) => ({
            Component: m.default,
          })),
      },
      // ─── Substrate Runtime (v19) ──────────────────────────────────────────
      {
        path: "substrate/poc",
        lazy: () =>
          import("@/features/substrate/pages/SubstratePocPage").then((m) => ({
            Component: m.SubstratePocPage,
          })),
      },
      {
        path: "substrate/actors",
        lazy: () =>
          import("@/features/substrate/pages/SubstrateActorPage").then((m) => ({
            Component: m.SubstrateActorPage,
          })),
      },
      {
        path: "substrate/kwp-workers",
        lazy: () =>
          import("@/features/substrate/pages/KwpWorkerPage").then((m) => ({
            Component: m.KwpWorkerPage,
          })),
      },
      {
        path: "substrate/kwp-agents",
        lazy: () =>
          import("@/features/substrate/pages/KwpAgentPage").then((m) => ({
            Component: m.KwpAgentPage,
          })),
      },
      {
        path: "substrate/observability",
        lazy: () =>
          import("@/features/substrate/pages/SubstrateObservabilityPage").then((m) => ({
            Component: m.SubstrateObservabilityPage,
          })),
      },
      {
        path: "substrate/audit",
        lazy: () =>
          import("@/features/substrate/pages/SubstrateAuditPage").then((m) => ({
            Component: m.SubstrateAuditPage,
          })),
      },
      {
        path: "substrate/sla",
        lazy: () =>
          import("@/features/substrate/pages/SubstrateSlaPage").then((m) => ({
            Component: m.SubstrateSlaPage,
          })),
      },
      {
        path: "substrate/security",
        lazy: () =>
          import("@/features/substrate/pages/SubstrateSecurityPage").then((m) => ({
            Component: m.SubstrateSecurityPage,
          })),
      },
      {
        path: "substrate/testing",
        lazy: () =>
          import("@/features/substrate/pages/SubstrateTestingPage").then((m) => ({
            Component: m.SubstrateTestingPage,
          })),
      },
      {
        path: "substrate/quota",
        lazy: () =>
          import("@/features/substrate/pages/SubstrateQuotaPage").then((m) => ({
            Component: m.SubstrateQuotaPage,
          })),
      },
      {
        path: "substrate/circuit-breakers",
        lazy: () =>
          import("@/features/substrate/pages/SubstrateCircuitBreakerPage").then((m) => ({
            Component: m.SubstrateCircuitBreakerPage,
          })),
      },
      {
        path: "substrate/cicd",
        lazy: () =>
          import("@/features/substrate/pages/SubstrateCicdPage").then((m) => ({
            Component: m.SubstrateCicdPage,
          })),
      },
      {
        path: "substrate/snapshots",
        lazy: () =>
          import("@/features/substrate/pages/SubstrateSnapshotPage").then((m) => ({
            Component: m.SubstrateSnapshotPage,
          })),
      },
      {
        path: "substrate/autoscaling",
        lazy: () =>
          import("@/features/substrate/pages/SubstrateAutoscalingPage").then((m) => ({
            Component: m.SubstrateAutoscalingPage,
          })),
      },
      {
        path: "substrate/dev-experience",
        lazy: () =>
          import("@/features/substrate/pages/SubstrateDevExperiencePage").then((m) => ({
            Component: m.SubstrateDevExperiencePage,
          })),
      },
      {
        path: "substrate/data-residency",
        lazy: () =>
          import("@/features/substrate/pages/SubstrateDataResidencyPage").then((m) => ({
            Component: m.SubstrateDataResidencyPage,
          })),
      },
    ],
  },


  // ─── War Room (v10) ────────────────────────────────────────────────────────
  {
    path: "/war-room",
    element: (
      <ProtectedRoute>
        <Suspense fallback={<PageSpinner />}>
          <WarRoomLayout />
        </Suspense>
      </ProtectedRoute>
    ),
    errorElement: <ErrorPage />,
    children: [
      {
        index: true,
        lazy: () =>
          import("@/features/war-room/pages/SessionListPage").then((m) => ({
            Component: m.SessionListPage,
          })),
      },
      {
        path: "new",
        lazy: () =>
          import("@/features/war-room/pages/CreateSessionPage").then((m) => ({
            Component: m.CreateSessionPage,
          })),
      },
      {
        path: ":sessionId",
        lazy: () =>
          import("@/features/war-room/pages/MasterDashboardPage").then((m) => ({
            Component: m.MasterDashboardPage,
          })),
      },
      {
        path: ":sessionId/team/:teamId",
        lazy: () =>
          import("@/features/war-room/pages/TeamPanelPage").then((m) => ({
            Component: m.TeamPanelPage,
          })),
      },
      {
        path: ":sessionId/pre-golive",
        lazy: () =>
          import("@/features/war-room/pages/PreGoLivePage").then((m) => ({
            Component: m.PreGoLivePage,
          })),
      },
      {
        path: ":sessionId/dry-run",
        lazy: () =>
          import("@/features/war-room/pages/DryRunPage").then((m) => ({
            Component: m.DryRunPage,
          })),
      },
      {
        path: ":sessionId/report",
        lazy: () =>
          import("@/features/war-room/pages/PostExecutionPage").then((m) => ({
            Component: m.PostExecutionPage,
          })),
      },
      {
        path: "history",
        lazy: () =>
          import("@/features/war-room/pages/HistoryPage").then((m) => ({
            Component: m.HistoryPage,
          })),
      },
      {
        path: "observability",
        lazy: () =>
          import("@/features/war-room/pages/ObservabilityPage").then((m) => ({
            Component: m.ObservabilityPage,
          })),
      },
    ],
  },

  // ═══════════════════════════════════════════════════════════════
  // SIDEBAR V21 — DBA Workspace (RTP-001)
  // ═══════════════════════════════════════════════════════════════
  {
    path: "/dba",
    element: (
      <DBAGuard>
        <Outlet />
      </DBAGuard>
    ),
    children: [
      { index: true, element: <Navigate to="/dba/dashboard" replace /> },
      {
        path: "dashboard",
        lazy: () =>
          import("@/features/dba-workspace/pages/DBADashboardPage").then((m) => ({
            Component: m.default,
          })),
      },
      {
        path: "query",
        lazy: () =>
          import("@/features/dba-workspace/pages/DBAQueryPage").then((m) => ({
            Component: m.default,
          })),
      },
      {
        path: "slow-queries",
        lazy: () =>
          import("@/features/dba-workspace/pages/SlowQueriesPage").then((m) => ({
            Component: m.default,
          })),
      },
      {
        path: "schema",
        lazy: () =>
          import("@/features/dba-workspace/pages/SchemaExplorerPage").then((m) => ({
            Component: m.default,
          })),
      },
      {
        path: "migrations",
        lazy: () =>
          import("@/features/dba-workspace/pages/MigrationsPage").then((m) => ({
            Component: m.default,
          })),
      },
      {
        path: "perf",
        lazy: () =>
          import("@/features/dba-workspace/pages/PerfMonitorPage").then((m) => ({
            Component: m.default,
          })),
      },
      {
        path: "connections",
        lazy: () =>
          import("@/features/dba-workspace/pages/ConnectionsPage").then((m) => ({
            Component: m.default,
          })),
      },
      {
        path: "access",
        lazy: () =>
          import("@/features/dba-workspace/pages/AccessControlPage").then((m) => ({
            Component: m.default,
          })),
      },
      {
        path: "audit",
        lazy: () =>
          import("@/features/dba-workspace/pages/AuditLogPage").then((m) => ({
            Component: m.default,
          })),
      },
    ],
  },

  // ═══════════════════════════════════════════════════════════════
  // SIDEBAR V21 — War Room & Org Announcements
  // ═══════════════════════════════════════════════════════════════
  {
    path: "/war-room-list",
    lazy: () =>
      import("@/features/war-room/pages/WarRoomListPage").then((m) => ({
        Component: m.default,
      })),
  },
  {
    path: "/org/announcements",
    lazy: () =>
      import("@/features/announcements/pages/AnnouncementsPage").then((m) => ({
        Component: m.default,
      })),
  },

  // ═══════════════════════════════════════════════════════════════
  // SIDEBAR V21 — History & Pinned Items
  // ═══════════════════════════════════════════════════════════════
  {
    path: "/me/history",
    lazy: () =>
      import("@/features/context-recent/pages/HistoryPage").then((m) => ({
        Component: m.default,
      })),
  },
  {
    path: "/me/pinned",
    lazy: () =>
      import("@/features/pinned-items/pages/PinnedAllPage").then((m) => ({
        Component: m.default,
      })),
  },

  // ═══════════════════════════════════════════════════════════════
  // SIDEBAR V21 — Settings & Plugins
  // ═══════════════════════════════════════════════════════════════
  {
    path: "/settings/sidebar",
    lazy: () =>
      import("@/features/sidebar-settings/SidebarSettingsPage").then((m) => ({
        Component: m.default,
      })),
  },
  {
    // CRS-V21-OBS-001: Fleet Observability top-level route
    path: "/fleet-observability",
    lazy: () =>
      import("@/features/agent-registry/pages/legacy_admin/observability/FleetObservabilityPage").then((m) => ({
        Component: m.FleetObservabilityPage,
      })),
  },
  {
    path: "/settings/plugins",
    children: [
      {
        index: true,
        lazy: () =>
          import("@/features/plugins/pages/PluginMarketplacePage").then((m) => ({
            Component: m.default,
          })),
      },
      {
        path: ":id",
        lazy: () =>
          import("@/features/plugins/pages/PluginDetailPage").then((m) => ({
            Component: m.default,
          })),
      },
    ],
  },

  // ═══════════════════════════════════════════════════════════════
  // SIDEBAR V21 — Admin Navigation & Telemetry
  // ═══════════════════════════════════════════════════════════════
  {
    path: "/admin/sidebar/config",
    element: (
      <AdminGuard>
        <Outlet />
      </AdminGuard>
    ),
    children: [
      {
        index: true,
        lazy: () =>
          import("@/features/admin-nav-config/pages/AdminSidebarConfigPage").then((m) => ({
            Component: m.default,
          })),
      },
    ],
  },
  {
    path: "/admin/analytics/sidebar",
    element: (
      <AdminGuard>
        <Outlet />
      </AdminGuard>
    ),
    children: [
      {
        index: true,
        lazy: () =>
          import("@/features/admin-nav-config/pages/SidebarAnalyticsPage").then((m) => ({
            Component: m.default,
          })),
      },
    ],
  },
  {
    path: "/admin/settings/sso/role-mapping",
    element: (
      <AdminGuard>
        <Outlet />
      </AdminGuard>
    ),
    children: [
      {
        index: true,
        lazy: () =>
          import("@/features/admin-nav-config/pages/SSOMappingPage").then((m) => ({
            Component: m.default,
          })),
      },
    ],
  },
  {
    path: "/admin/config-management",
    element: (
      <AdminGuard>
        <Outlet />
      </AdminGuard>
    ),
    children: [
      {
        index: true,
        lazy: () =>
          import("@/features/admin-nav-config/pages/ConfigManagementPage").then((m) => ({
            Component: m.default,
          })),
      },
    ],
  },
  {
    path: "/admin/feature-flags",
    element: (
      <AdminGuard>
        <Outlet />
      </AdminGuard>
    ),
    children: [
      {
        index: true,
        lazy: () =>
          import("@/features/admin-nav-config/pages/FeatureFlagsAdminPage").then((m) => ({
            Component: m.default,
          })),
      },
    ],
  },

  // ─── Legacy redirects ─────────────────────────────────────────────────────
  { path: "/chat", element: <Navigate to="/" replace /> },
  // Matches both bare "/app" and any sub-path ("/app/agents", "/app/settings", ...).
  { path: "/app/*", element: <LegacyAppRedirect /> },
  { path: "/admin/assistants", element: <Navigate to="/admin/agents" replace /> },
  { path: "/admin/assistants/*", element: <Navigate to="/admin/agents" replace /> },
  { path: "/admin/configuration/llm", element: <Navigate to="/admin/configuration/language-models" replace /> },
  // /admin/configuration/search → web-search (old URL circulating in docs/bookmarks)
  { path: "/admin/configuration/search", element: <Navigate to="/admin/configuration/web-search" replace /> },

  // Backward compat — v6 context routes
  { path: '/plans', element: <Navigate to="/workplace/plans" replace /> },
  { path: '/plans/*', element: <SplatRedirect targetBase="/workplace/plans" /> },
  { path: '/home/settings', element: <Navigate to="/settings" replace /> },

  // ═══════════════════════════════════════════════════════════════
  // BUG-MISSING-V4-003 FIX — v20 & v21 missing route registrations
  // ═══════════════════════════════════════════════════════════════

  // CRS-V21-PLG-002: Plugin Marketplace (top-level shortcut)
  // /plugins/marketplace is the canonical URL used in tests & sidebar nav.
  // /settings/plugins already renders PluginMarketplacePage as index, but the
  // bare /plugins/marketplace path was not registered and returned a 404.
  {
    path: '/plugins/marketplace',
    lazy: () =>
      import('@/features/plugins/pages/PluginMarketplacePage').then((m) => ({
        Component: m.default,
      })),
  },

  // CRS-V20-ADM-001: AI Agents Session Monitoring admin dashboard.
  // Tests and sidebar navigate to /admin/ai/agent-sessions; the page was
  // registered at /admin/ai-agents (nested under /admin). Redirect the
  // alias so both URLs resolve to the AdminAiAgentsPage.
  {
    path: '/admin/ai/agent-sessions',
    element: <Navigate to="/admin/ai-agents" replace />,
  },

  // CRS-V20-PTU-001: Persona Tool Profiles admin list page.
  // No page was registered for /admin/personas; PersonaProfilesPage is the
  // new admin landing view for the persona-tool-profile feature.
  {
    path: '/admin/personas',
    lazy: () =>
      import('@/features/admin/pages/PersonaProfilesPage').then((m) => ({
        Component: m.PersonaProfilesPage,
      })),
  },
  {
    // Detail route: /admin/personas/:id/tools — renders the per-persona
    // tool profile editor tab (SOL-UI-PTU-001).
    path: '/admin/personas/:id/tools',
    lazy: () =>
      import('@/features/admin/pages/PersonaProfilesPage').then((m) => ({
        Component: m.PersonaProfilesPage,
      })),
  },

  // CRS-V20-MCM-001: Memory Management Settings
  // /settings/memories (plural) was not registered; MemorySettingsPage
  // (storage-mode / consent / retention tabs) is the correct target.
  {
    path: '/settings/memories',
    lazy: () =>
      import('@/features/memory/pages/MemorySettingsPage').then((m) => ({
        Component: m.MemorySettingsPage,
      })),
  },

  // CRS-V20-TCG-001: Toolcall Approval Queue alias
  // Tests use /admin/toolcall-approvals; the page is registered at
  // /admin/tool-approvals (inside the /admin parent route). Redirect the
  // plural/hyphenated alias.
  {
    path: '/admin/toolcall-approvals',
    element: <Navigate to="/admin/tool-approvals" replace />,
  },

  // CRS-V21-CFG-001: Config Management short alias
  // /admin/config-management is the canonical path; /admin/config is the
  // short alias used in sidebar nav and tests.
  {
    path: '/admin/config',
    element: <Navigate to="/admin/config-management" replace />,
  },


  // ═══════════════════════════════════════════════════════════════
  // RESTORED ORPHAN PAGES — CRS Cross-reference 2026-10-21
  // All pages restored from _quarantined/ and wired per design specs
  // ═══════════════════════════════════════════════════════════════

  // ─── War Room Detail (CRS v21/WAR-001) ───────────────────────────────────
  // WarRoomDetailPage was missing from /war-room/:sessionId route group
  {
    path: '/war-room/:sessionId/detail',
    element: (
      <ProtectedRoute>
        <Suspense fallback={<PageSpinner />}>
          <WarRoomLayout />
        </Suspense>
      </ProtectedRoute>
    ),
    children: [
      {
        index: true,
        lazy: () =>
          import('@/features/war-room/pages/WarRoomDetailPage').then((m) => ({
            Component: m.WarRoomDetailPage,
          })),
      },
    ],
  },

  // ─── DBA Workspace additions (CRS v12/v21) ───────────────────────────────
  // SchemaDiffPage + dba/* pages wired under existing /dba DBAGuard block
  // Additional DBA pages not in the original /dba children:
  {
    path: '/dba/diff',
    element: <DBAGuard><Outlet /></DBAGuard>,
    children: [
      {
        index: true,
        lazy: () =>
          import('@/features/dba-workspace/pages/SchemaDiffPage').then((m) => ({
            Component: m.SchemaDiffPage,
          })),
      },
    ],
  },
  {
    path: '/dba/change-management',
    element: <DBAGuard><Outlet /></DBAGuard>,
    children: [
      {
        index: true,
        lazy: () =>
          import('@/features/dba/pages/ChangeManagementPage').then((m) => ({
            Component: m.ChangeManagementPage,
          })),
      },
    ],
  },
  {
    path: '/dba/sql',
    element: <DBAGuard><Outlet /></DBAGuard>,
    children: [
      {
        index: true,
        lazy: () =>
          import('@/features/dba/pages/SQLExecutorPage').then((m) => ({
            Component: m.SQLExecutorPage,
          })),
      },
    ],
  },

  // ─── Developer Workspace (CRS v21/RTP-002) ───────────────────────────────
  {
    path: '/dev',
    element: (
      <DevGuard>
        <Outlet />
      </DevGuard>
    ),
    children: [
      { index: true, element: <Navigate to="/dev/sandbox" replace /> },
      {
        path: 'sandbox',
        lazy: () =>
          import('@/features/dev-workspace/pages/DevSandboxPage').then((m) => ({
            Component: m.DevSandboxPage,
          })),
      },
      {
        path: 'debug',
        lazy: () =>
          import('@/features/dev-workspace/pages/DebugPage').then((m) => ({
            Component: m.DebugPage,
          })),
      },
      {
        path: 'prs',
        lazy: () =>
          import('@/features/dev-workspace/pages/PRReviewPage').then((m) => ({
            Component: m.PRReviewPage,
          })),
      },
      {
        path: 'tests',
        lazy: () =>
          import('@/features/dev-workspace/pages/TestRunnerPage').then((m) => ({
            Component: m.TestRunnerPage,
          })),
      },
      {
        path: 'api',
        lazy: () =>
          import('@/features/dev-workspace/pages/APIExplorerPage').then((m) => ({
            Component: m.APIExplorerPage,
          })),
      },
      {
        path: 'sdk-playground',
        lazy: () =>
          import('@/features/sdk-playground/components/SDKPlaygroundPage').then((m) => ({
            Component: m.SDKPlaygroundPage,
          })),
      },
    ],
  },
  // Dev Dashboard (CRS v18/12-developer-exp)
  {
    path: '/dev/dashboard',
    element: <DevGuard><Outlet /></DevGuard>,
    children: [
      {
        index: true,
        lazy: () =>
          import('@/features/dev-sandbox/components/DevDashboardPage').then((m) => ({
            Component: m.DevDashboardPage,
          })),
      },
    ],
  },

  // ─── Product Manager Workspace (CRS v21/RTP-002, v13) ───────────────────
  {
    path: '/sprint',
    lazy: () =>
      import('@/features/product-workspace/pages/SprintBoardPage').then((m) => ({
        Component: m.SprintBoardPage,
      })),
  },
  {
    path: '/okrs',
    lazy: () =>
      import('@/features/product-workspace/pages/OKRsPage').then((m) => ({
        Component: m.OKRsPage,
      })),
  },
  {
    path: '/products/roadmap',
    lazy: () =>
      import('@/features/product-workspace/pages/ProductRoadmapPage').then((m) => ({
        Component: m.ProductRoadmapPage,
      })),
  },
  {
    path: '/plans/board',
    lazy: () =>
      import('@/features/product-workspace/pages/PlansPage').then((m) => ({
        Component: m.PlansPage,
      })),
  },
  {
    path: '/projects/personal',
    lazy: () =>
      import('@/features/projects/pages/PersonalProjectDashboardPage').then((m) => ({
        Component: m.PersonalProjectDashboardPage,
      })),
  },

  // ─── Products Knowledge Base (CRS v16/products-admin-ui) ────────────────
  {
    path: '/products/:productId',
    children: [
      {
        path: 'architecture',
        lazy: () =>
          import('@/features/products/products/architecture/ArchitecturePage').then((m) => ({
            Component: m.ArchitecturePage,
          })),
      },
      {
        path: 'architecture/adrs/:adrId',
        lazy: () =>
          import('@/features/products/products/architecture/ADRViewerPage').then((m) => ({
            Component: m.ADRViewerPage,
          })),
      },
      {
        path: 'architecture/hld',
        lazy: () =>
          import('@/features/products/products/architecture/HLDViewerPage').then((m) => ({
            Component: m.HLDViewerPage,
          })),
      },
      {
        path: 'business',
        lazy: () =>
          import('@/features/products/products/business/BusinessPage').then((m) => ({
            Component: m.BusinessPage,
          })),
      },
      {
        path: 'docs',
        lazy: () =>
          import('@/features/products/products/docs/DocumentLibraryPage').then((m) => ({
            Component: m.DocumentLibraryPage,
          })),
      },
      {
        path: 'docs/:docId',
        lazy: () =>
          import('@/features/products/products/docs/DocumentViewerPage').then((m) => ({
            Component: m.DocumentViewerPage,
          })),
      },
      {
        path: 'features',
        lazy: () =>
          import('@/features/products/products/features/FeaturesPage').then((m) => ({
            Component: m.FeaturesPage,
          })),
      },
      {
        path: 'features/:featureId',
        lazy: () =>
          import('@/features/products/products/features/FeatureDetailPage').then((m) => ({
            Component: m.FeatureDetailPage,
          })),
      },
      {
        path: 'roadmap',
        lazy: () =>
          import('@/features/products/products/roadmap/RoadmapPage').then((m) => ({
            Component: m.RoadmapPage,
          })),
      },
    ],
  },

  // ─── Org Management (CRS v13/identity-organization-service) ─────────────
  {
    path: '/org',
    children: [
      {
        path: 'settings',
        lazy: () =>
          import('@/features/org/pages/OrgSettingsPage').then((m) => ({
            Component: m.OrgSettingsPage,
          })),
      },
      {
        path: 'members',
        lazy: () =>
          import('@/features/org/pages/MemberDirectoryPage').then((m) => ({
            Component: m.MemberDirectoryPage,
          })),
      },
      {
        path: 'roles',
        lazy: () =>
          import('@/features/org/pages/RoleManagementPage').then((m) => ({
            Component: m.RoleManagementPage,
          })),
      },
      {
        path: 'gdpr',
        lazy: () =>
          import('@/features/org/pages/GDPRExportPage').then((m) => ({
            Component: m.GDPRExportPage,
          })),
      },
    ],
  },

  // ─── Notifications (new-pages.md §Notifications) ─────────────────────────
  // Standalone /notifications — TDD spec: "authenticated, list notifications,
  // mark read, filter by type". Was previously only nested under /settings.
  {
    path: '/notifications',
    lazy: () =>
      import('@/features/settings/pages/personal/NotificationsPage').then((m) => ({
        Component: m.NotificationsPage,
      })),
  },

  // ─── Meetings standalone (new-pages.md §Meetings) ────────────────────────
  // Standalone /meetings — TDD spec: "meeting scheduler, calendar integration".
  // meeting feature has MeetingListPage; /meetings route was missing at top level.
  {
    path: '/meetings',
    lazy: () =>
      import('@/features/meeting/pages/MeetingListPage').then((m) => ({
        Component: m.MeetingListPage,
      })),
  },

  // ─── Plugins standalone (new-pages.md §Plugins) ──────────────────────────
  // Standalone /plugins — TDD spec: "plugin marketplace, browse, install".
  // /settings/plugins already exists; this adds the top-level canonical path.
  {
    path: '/plugins',
    lazy: () =>
      import('@/features/plugins/pages/PluginsListPage').then((m) => ({
        Component: m.default,
      })),
  },
  {
    path: '/plugins/:id',
    lazy: () =>
      import('@/features/plugins/pages/PluginDetailPage').then((m) => ({
        Component: m.default,
      })),
  },

  // ─── Campaigns (CRS v11/campaign-mcp-tools, v6/ui-upgrade) ──────────────
  {
    path: '/campaigns',
    lazy: () =>
      import('@/features/campaigns/pages/CampaignsPage').then((m) => ({
        Component: m.CampaignsPage,
      })),
  },
  {
    path: '/workplace/campaigns',
    lazy: () =>
      import('@/features/workplace/pages/CampaignsListPage').then((m) => ({
        Component: m.CampaignsListPage,
      })),
  },
  {
    path: '/workplace/plans',
    lazy: () =>
      import('@/features/workplace/pages/PlansListPage').then((m) => ({
        Component: m.PlansListPage,
      })),
  },
  {
    path: '/workplace/shared-projects',
    lazy: () =>
      import('@/features/workplace/pages/SharedProjectsPage').then((m) => ({
        Component: m.SharedProjectsPage,
      })),
  },

  // ─── Change Requests Board (internal) ───────────────────────────────────
  {
    path: '/change-requests',
    lazy: () =>
      import('@/features/change-requests/pages/ChangeRequestBoard').then((m) => ({
        Component: m.ChangeRequestBoard,
      })),
  },

  // ─── Connectors (CRS v17/connector-ecosystem-expansion) ─────────────────
  {
    path: '/admin/connectors/catalog',
    element: <AdminGuard><Outlet /></AdminGuard>,
    children: [
      {
        index: true,
        lazy: () =>
          import('@/features/connectors/pages/ConnectorCatalogPage').then((m) => ({
            Component: m.ConnectorCatalogPage,
          })),
      },
    ],
  },

  // ─── Memory (CRS v9/memory, v20/MCM-001) ────────────────────────────────
  {
    path: '/me/memory',
    lazy: () =>
      import('@/features/memory/pages/MemoryPage').then((m) => ({
        Component: m.MemoryPage,
      })),
  },
  {
    path: '/admin/memory',
    element: <AdminGuard><Outlet /></AdminGuard>,
    children: [
      {
        index: true,
        lazy: () =>
          import('@/features/memory/pages/MemoryAdminPage').then((m) => ({
            Component: m.MemoryAdminPage,
          })),
      },
    ],
  },
  {
    path: '/me/memory-bank',
    lazy: () =>
      import('@/features/memory-bank/pages/MemoryBankPage').then((m) => ({
        Component: m.MemoryBankPage,
      })),
  },

  // ─── Policy extensions (CRS v8/v12) — sub-pages added to existing admin/policy block
  // admin/policy/* are children of the existing nested 'policy' route inside /admin.
  // These standalone routes cover the NEW pages from features/policy/pages/ that
  // were not part of the original admin/pages/policy/* set.
  {
    path: '/admin/vnp-policy/gateway',
    element: <AdminGuard><Outlet /></AdminGuard>,
    children: [
      {
        path: 'compliance',
        lazy: () =>
          import('@/features/policy/pages/ComplianceReportPage').then((m) => ({
            Component: m.ComplianceReportPage,
          })),
      },
      {
        path: 'conflicts',
        lazy: () =>
          import('@/features/policy/pages/ConflictDetectorPage').then((m) => ({
            Component: m.ConflictDetectorPage,
          })),
      },
      {
        path: 'debug',
        lazy: () =>
          import('@/features/policy/pages/PolicyDebugPage').then((m) => ({
            Component: m.PolicyDebugPage,
          })),
      },
      {
        path: ':policyId',
        lazy: () =>
          import('@/features/policy/pages/PolicyDetailPage').then((m) => ({
            Component: m.PolicyDetailPage,
          })),
      },
    ],
  },

  // ─── Security extensions (CRS v8/v12) — CVEListPage & ReportingAuditPage
  // from features/security/pages/ (distinct from features/admin/pages/security/)
  {
    path: '/admin/vnp-security/gateway',
    element: <AdminGuard><Outlet /></AdminGuard>,
    children: [
      {
        path: 'cves',
        lazy: () =>
          import('@/features/security/pages/CVEListPage').then((m) => ({
            Component: m.CVEListPage,
          })),
      },
      {
        path: 'reporting',
        lazy: () =>
          import('@/features/security/pages/ReportingAuditPage').then((m) => ({
            Component: m.ReportingAuditPage,
          })),
      },
    ],
  },

  // ─── Skills Catalog (CRS v12/skills-auto-invoke) ─────────────────────────
  // NOTE: /settings/skills-catalog already exists (line 416) for SkillsCatalog wrapper.
  // SkillCatalogPage is the standalone full catalog — use /skills/catalog.
  {
    path: '/skills/catalog',
    lazy: () =>
      import('@/features/skills/pages/SkillCatalogPage').then((m) => ({
        Component: m.SkillCatalogPage,
      })),
  },

  // ─── Tools Discovery (CRS v17/agent-tool-discovery) ─────────────────────
  {
    path: '/tools/discover',
    lazy: () =>
      import('@/features/tools/pages/ToolDiscoveryPage').then((m) => ({
        Component: m.ToolDiscoveryPage,
      })),
  },

  // ─── OpenPanel (CRS v12/openpanel-gateway-service) ──────────────────────
  {
    path: '/admin/openpanel',
    element: <AdminGuard><Outlet /></AdminGuard>,
    children: [
      {
        index: true,
        lazy: () =>
          import('@/features/openpanel/pages/OpenPanelDashboardPage').then((m) => ({
            Component: m.OpenPanelDashboardPage,
          })),
      },
      {
        path: 'events',
        lazy: () =>
          import('@/features/openpanel/pages/EventStreamPage').then((m) => ({
            Component: m.EventStreamPage,
          })),
      },
    ],
  },

  // ─── Observability/Tracing (CRS v13/observability-tracing) ──────────────
  {
    path: '/admin/traces',
    element: <AdminGuard><Outlet /></AdminGuard>,
    children: [
      {
        index: true,
        lazy: () =>
          import('@/features/admin/pages/TracingPage').then((m) => ({
            Component: m.TracingPage,
          })),
      },
      {
        path: ':traceId',
        lazy: () =>
          import('@/features/admin/pages/TraceDetailPage').then((m) => ({
            Component: m.TraceDetailPage,
          })),
      },
    ],
  },

  // ─── Platform Health & Tenants (CRS v18/platform-ops, v18/multi-tenancy) ─
  {
    path: '/admin/platform/health',
    element: <AdminGuard><Outlet /></AdminGuard>,
    children: [
      {
        index: true,
        lazy: () =>
          import('@/features/admin/platform/health/PlatformHealthPage').then((m) => ({
            Component: m.PlatformHealthPage,
          })),
      },
    ],
  },
  {
    path: '/admin/tenants',
    element: <AdminGuard><Outlet /></AdminGuard>,
    children: [
      {
        index: true,
        lazy: () =>
          import('@/features/admin/tenants/TenantAdminPage').then((m) => ({
            Component: m.TenantAdminPage,
          })),
      },
    ],
  },

  // ─── Agent Registry / Control Plane (CRS v18) ───────────────────────────
  {
    path: '/admin/agent-runtime',
    element: <AdminGuard><Outlet /></AdminGuard>,
    children: [
      {
        path: 'actors',
        lazy: () =>
          import('@/features/agent-registry/pages/legacy_admin/ActorPoolPage').then((m) => ({
            Component: m.ActorPoolPage,
          })),
      },
      {
        path: 'orchestration',
        lazy: () =>
          import('@/features/agent-registry/pages/legacy_admin/AgentOrchestrationPage').then((m) => ({
            Component: m.AgentOrchestrationPage,
          })),
      },
      {
        path: 'tasks',
        lazy: () =>
          import('@/features/agent-registry/pages/legacy_admin/AgenticTaskListPage').then((m) => ({
            Component: m.AgenticTaskListPage,
          })),
      },
      {
        path: 'tasks/:taskId',
        lazy: () =>
          import('@/features/agent-registry/pages/legacy_admin/AgenticTaskDetailPage').then((m) => ({
            Component: m.AgenticTaskDetailPage,
          })),
      },
      {
        path: 'traces',
        lazy: () =>
          import('@/features/agent-registry/pages/legacy_admin/AgenticTraceExplorerPage').then((m) => ({
            Component: m.AgenticTraceExplorerPage,
          })),
      },
      {
        path: 'sandbox',
        lazy: () =>
          import('@/features/agent-registry/pages/legacy_admin/SandboxMonitorPage').then((m) => ({
            Component: m.SandboxMonitorPage,
          })),
      },
      {
        path: 'warm-pool',
        lazy: () =>
          import('@/features/agent-registry/pages/legacy_admin/sandboxes/WarmPoolPage').then((m) => ({
            Component: m.WarmPoolPage,
          })),
      },
      {
        path: 'fleet',
        lazy: () =>
          import('@/features/agent-registry/pages/legacy_admin/observability/FleetObservabilityPage').then((m) => ({
            Component: m.FleetObservabilityPage,
          })),
      },
      {
        path: 'debug',
        lazy: () =>
          import('@/features/agent-registry/pages/legacy_admin/tasks/AgentDebugPage').then((m) => ({
            Component: m.AgentDebugPage,
          })),
      },
    ],
  },
  // agent-runtime sub-pages below are merged INTO /admin/agent-runtime children above.
  // /admin/agent-runtime/security-policy → handled in /admin/agent-runtime block
  // /admin/agent-runtime/budget-monitor  → handled in /admin/agent-runtime block
  // /admin/agent-runtime/audit           → handled in /admin/agent-runtime block
  // /admin/agent-runtime/runtime-policy  → handled in /admin/agent-runtime block
  // /admin/agent-runtime/tool-approvals  → NOTE: /admin/tool-approvals already exists
  // /admin/agent-runtime/tool-catalog    → NOTE: /admin/tools/catalog is new

  // ─── Distributed Search (CRS v9/distributed-search) ─────────────────────
  {
    path: '/admin/distributed-search/vectors',
    element: <AdminGuard><Outlet /></AdminGuard>,
    children: [
      {
        index: true,
        lazy: () =>
          import('@/features/distributed-search/admin/pages/VectorRegistryAdminPage').then((m) => ({
            Component: m.VectorRegistryAdminPage,
          })),
      },
    ],
  },
  // /admin/search/agents — TDD distributed-search-pages.md §SearchAgentsAdminPage
  // AgentHealthGrid + SearchAuditLog for org-wide distributed search monitoring
  {
    path: '/admin/search/agents',
    element: <AdminGuard><Outlet /></AdminGuard>,
    children: [
      {
        index: true,
        lazy: () =>
          import('@/features/distributed-search/admin/pages/DistributedSearchAdminPage').then((m) => ({
            Component: m.DistributedSearchAdminPage,
          })),
      },
    ],
  },

  // ─── Google Drive (CRS v10/google-drive-v2) ──────────────────────────────
  // BUG-GOOG-002 FIX: Drive Explorer standalone route
  // BUG-GOOG3-004 FIX: wrap with ProtectedRoute so unauthenticated users are
  // redirected to /auth/login instead of seeing the Drive UI directly.
  // implements: CRS-GOOG-002
  {
    path: '/drive',
    element: (
      <ProtectedRoute>
        <AppLayout />
      </ProtectedRoute>
    ),
    errorElement: <ErrorPage />,
    children: [
      {
        index: true,
        lazy: () =>
          import('@/features/google-drive/pages/DriveExplorerPage').then((m) => ({
            Component: m.DriveExplorerPage,
          })),
      },
    ],
  },
  {
    path: '/integrations/google-drive',
    children: [
      {
        index: true,
        lazy: () =>
          import('@/features/google-drive/pages/GoogleDriveDashboardPage').then((m) => ({
            Component: m.GoogleDriveDashboardPage,
          })),
      },
      {
        path: 'setup',
        lazy: () =>
          import('@/features/google-drive/pages/GoogleDriveSetupPage').then((m) => ({
            Component: m.GoogleDriveSetupPage,
          })),
      },
    ],
  },

  // ─── Meeting pages (CRS v19/03-meeting-capture, v10/team-meeting) ────────
  // GCalOAuthCallbackPage kept in place (not quarantined) — routed separately
  {
    path: '/meetings/series',
    lazy: () =>
      import('@/features/meeting/pages/SeriesPage').then((m) => ({
        Component: m.SeriesPage,
      })),
  },
  {
    path: '/meetings/series/:seriesId',
    lazy: () =>
      import('@/features/meeting/pages/SeriesDashboard').then((m) => ({
        Component: m.SeriesDashboard,
      })),
  },
  {
    path: '/meetings/:meetingId/actions',
    lazy: () =>
      import('@/features/meeting/pages/AIActionItemsPage').then((m) => ({
        Component: m.AIActionItemsPage,
      })),
  },
  {
    path: '/meetings/:meetingId/attendance',
    lazy: () =>
      import('@/features/meeting/pages/AttendancePage').then((m) => ({
        Component: m.AttendancePage,
      })),
  },
  {
    path: '/settings/meeting-policy',
    lazy: () =>
      import('@/features/meeting/pages/WorkspacePolicySettingsPage').then((m) => ({
        Component: m.WorkspacePolicySettingsPage,
      })),
  },

  // ─── Workspace features (CRS v12/v13) ────────────────────────────────────
  {
    path: '/settings/desktop-agent',
    lazy: () =>
      import('@/features/settings/pages/DesktopAgentSettingsPage').then((m) => ({
        Component: m.DesktopAgentSettingsPage,
      })),
  },
  // /settings/desktop-agent/search — distributed search consent + agent status
  // TDD distributed-search-pages.md: ConsentFolderList + AgentStatusHeader
  {
    path: '/settings/desktop-agent/search',
    lazy: () =>
      import('@/features/distributed-search/settings/DesktopAgentsPage').then((m) => ({
        Component: m.DesktopAgentsPage,
      })),
  },
  {
    path: '/settings/scheduled-tasks',
    lazy: () =>
      import('@/features/tasks/pages/ScheduledTasksPage').then((m) => ({
        Component: m.ScheduledTasksPage,
      })),
  },
  {
    path: '/support',
    lazy: () =>
      import('@/features/support/ui/SupportPage').then((m) => ({
        Component: m.SupportPage,
      })),
  },
  {
    path: '/admin/dev-servers',
    element: <AdminGuard><Outlet /></AdminGuard>,
    children: [
      {
        index: true,
        lazy: () =>
          import('@/features/dev-tools/pages/DevServersAdminPage').then((m) => ({
            Component: m.DevServersAdminPage,
          })),
      },
    ],
  },

  // ─── Workspace KV Store (CRS v12/workspace-kv-store) ────────────────────
  {
    path: '/workspace-kv',
    lazy: () =>
      import('@/features/workspace-kv/pages/WorkspaceKVPage').then((m) => ({
        Component: m.WorkspaceKVPage,
      })),
  },

  // ─── Workspace-scoped features (CRS v12/v13) ────────────────────────────
  // These are nested under ws/:workspaceId in the main ProtectedRoute
  // Added as standalone routes until workspace layout integration

  // WorkspaceKV (CRS v12)
  {
    path: '/ws/:workspaceId/kv',
    lazy: () =>
      import('@/features/workspace-kv/pages/WorkspaceKVPage').then((m) => ({
        Component: m.WorkspaceKVPage,
      })),
  },
  // Workspace Graph (CRS v13/workspace-graph-data-model)
  {
    path: '/ws/:workspaceId/graph',
    lazy: () =>
      import('@/features/workspace/pages/WorkspaceGraphPage').then((m) => ({
        Component: m.WorkspaceGraphPage,
      })),
  },
  // Workspace Members extended (CRS v13/ui-org-workspace)
  {
    path: '/ws/:workspaceId/all-members',
    lazy: () =>
      import('@/features/workspace/pages/WorkspaceMembersPage').then((m) => ({
        Component: m.WorkspaceMembersPage,
      })),
  },
  // Workspace Plans (CRS v13/plan-okr-workspace-integration)
  {
    path: '/ws/:workspaceId/plans',
    lazy: () =>
      import('@/features/workspace/pages/plans/WorkspacePlansPage').then((m) => ({
        Component: m.WorkspacePlansPage,
      })),
  },
  // Workspace AI Settings (CRS v13/ai-model-routing-workspace)
  {
    path: '/ws/:workspaceId/settings/ai',
    lazy: () =>
      import('@/features/workspace/pages/settings/WorkspaceAISettingsPage').then((m) => ({
        Component: m.WorkspaceAISettingsPage,
      })),
  },
  // AI Provider Settings (CRS v13/workspace-aware-ai-provider)
  {
    path: '/ws/:workspaceId/settings/ai-provider',
    lazy: () =>
      import('@/features/workspace/pages/AIProviderSettingsPage').then((m) => ({
        Component: m.AIProviderSettingsPage,
      })),
  },
  // Intent Page (CRS v13/intent-centric-orchestration)
  {
    path: '/ws/:workspaceId/intent',
    lazy: () =>
      import('@/features/workspace/pages/IntentPage').then((m) => ({
        Component: m.IntentPage,
      })),
  },
  // Customer/Partner workspace types (CRS v13)
  {
    path: '/ws/:workspaceId/customer-partner',
    lazy: () =>
      import('@/features/workspace/pages/CustomerPartnerWorkspacePage').then((m) => ({
        Component: m.CustomerPartnerWorkspacePage,
      })),
  },
  // Workspace Create (standalone)
  {
    path: '/workspaces/create',
    lazy: () =>
      import('@/features/workspace/pages/CreateWorkspacePage').then((m) => ({
        Component: m.CreateWorkspacePage,
      })),
  },

  // ─── Meeting GCal OAuth Callback (CRS v19/meeting) ──────────────────────
  // GCal OAuth is active — must have dedicated route
  {
    path: '/meetings/gcal/callback',
    lazy: () =>
      import('@/features/meeting/pages/GCalOAuthCallbackPage').then((m) => ({
        Component: m.GCalOAuthCallbackPage,
      })),
  },

  // ─── Workflow (CRS v13/workflow-workspace-isolation) ─────────────────────
  {
    path: '/workflows',
    lazy: () =>
      import('@/features/workflow/pages/WorkflowPage').then((m) => ({
        Component: m.WorkflowPage,
      })),
  },
  {
    path: '/ws/:workspaceId/workflow',
    lazy: () =>
      import('@/features/workflow/pages/WorkflowPage').then((m) => ({
        Component: m.WorkflowPage,
      })),
  },

  // ─── Route Aliases & Redirects for Audit / Legacy Compatibility ──────────
  { path: "/im", element: <Navigate to="/admin/im" replace /> },
  { path: "/im/:conversationId", element: <Navigate to="/messaging/:conversationId" replace /> },

  // Security short aliases (TASK-UI-V5-002: BUG-UI-V5-066..070)
  { path: "/security", element: <Navigate to="/admin/vnp-security" replace /> },
  { path: "/security/cve", element: <Navigate to="/admin/vnp-security/cve" replace /> },
  { path: "/security/findings", element: <Navigate to="/admin/vnp-security/findings" replace /> },
  { path: "/security/scans", element: <Navigate to="/admin/vnp-security/scan" replace /> },
  { path: "/security/reports", element: <Navigate to="/admin/vnp-security/reports" replace /> },

  // Policy short aliases (TASK-UI-V5-003: BUG-UI-V5-076..080)
  { path: "/policies", element: <Navigate to="/admin/policy/policies" replace /> },
  { path: "/policies/debug", element: <Navigate to="/admin/vnp-policy/gateway/debug" replace /> },
  { path: "/policies/conflicts", element: <Navigate to="/admin/vnp-policy/gateway/conflicts" replace /> },
  { path: "/policies/compliance", element: <Navigate to="/admin/vnp-policy/gateway/compliance" replace /> },
  { path: "/policies/:policyId", element: <PolicyParamRedirect /> },
  { path: "/policies/:id", element: <PolicyParamRedirect /> },

  // MCP / Proxy / DBA aliases (TASK-UI-V5-004: BUG-UI-V5-086, 091..093)
  { path: "/admin/mcp-servers", element: <Navigate to="/admin/proxy/mcp-servers" replace /> },
  { path: "/admin/observability", element: <Navigate to="/admin/proxy/observability" replace /> },
  { path: "/admin/integrations", element: <Navigate to="/admin/vnp-dba" replace /> },
  { path: "/admin/integrations/dba", element: <Navigate to="/admin/vnp-dba/instances" replace /> },

  // Workflow & Agent Registry aliases (TASK-UI-V5-005: BUG-UI-V5-045, 046, 106..109)
  { path: "/app/workflows", element: <Navigate to="/workflows" replace /> },
  { path: "/app/workflows/:id/builder", element: <Navigate to="/workflows/new" replace /> },
  { path: "/app/workflows/:id/runs", element: <Navigate to="/workflows" replace /> },
  { path: "/agents/registry", element: <Navigate to="/admin/agents/registry" replace /> },
  { path: "/agents/registry/:id", element: <AgentRegistryRedirect /> },

  // ─── 404 ──────────────────────────────────────────────────────────────────
  { path: "*", element: <ErrorPage status={404} /> },
];
